import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch, apiFetchJson } from "../api";
import StyledSelect from "./StyledSelect";
import { SatisfactionBadge } from "./AccountTile";
import {
  XIcon,
  ChevronRightIcon,
  LoadingIcon,
  FileIcon,
  PackageIcon,
  SendIcon,
  AlertIcon,
  SearchIcon,
  SparklesIcon,
  ChevronLeftIcon,
  StarIcon,
  CheckIcon,
  UserIcon,
  LinkIcon,
  CropIcon
} from "./Icons";
import PhotoEditor from "./PhotoEditor";
import AdPreviewDialog from "./AdPreviewDialog";
import { t, tf, locale } from "../i18n";
import { catText, useCategoryLang } from "../categoryLang";
import { useAiPrefs } from "../utils/aiPrefs";
import { lockBodyScroll, unlockBodyScroll, useModalTouchGuard } from "../utils/bodyScrollLock";
import { useModalPresence, overlayMotionProps, dialogMotionProps } from "../utils/modalPresence";
import { useEscapeLayer } from "../utils/escapeLayers";
import { A, aa, S, sa, onAccent } from "../theme/palette";
import { buildPhotoSequence, moveInSequence } from "../utils/photoOrder";

const AD_MODAL_DEBUG = false;
const adModalDebugLog = (...args) => {
  if (!AD_MODAL_DEBUG) return;
  console.log(...args);
};
const adModalDebugWarn = (...args) => {
  if (!AD_MODAL_DEBUG) return;
  console.warn(...args);
};
const adModalDebugError = (...args) => {
  if (!AD_MODAL_DEBUG) return;
  console.error(...args);
};
const EBAY_IMPORT_MAX_IMAGES = 20;
// Сколько фото в объявлении всего — вместе с фото черновика (счётчик «из 20»).
const AD_MAX_IMAGES = 20;
const EBAY_ITEM_URL_PATTERN = /^https?:\/\/(?:www\.)?ebay\.[^/]+\/.+/i;

// Площадки граббера. eBay остаётся на своём маршруте — у него отдельный разбор,
// выросший вокруг его же особенностей; остальные идут общим.
const GRAB_SITES = [
  { id: "ebay", label: "eBay", pattern: /(^|\.)ebay\.[a-z.]+$/i, hint: "ebay.de/itm/..." },
  { id: "kleinanzeigen", label: "Kleinanzeigen", pattern: /(^|\.)kleinanzeigen\.de$/i, hint: "kleinanzeigen.de/s-anzeige/..." },
  { id: "willhaben", label: "willhaben", pattern: /(^|\.)willhaben\.at$/i, hint: "willhaben.at/iad/..." },
  { id: "marktplaats", label: "Marktplaats", pattern: /(^|\.)marktplaats\.nl$/i, hint: "marktplaats.nl/v/..." },
  { id: "ricardo", label: "Ricardo", pattern: /(^|\.)ricardo\.ch$/i, hint: "ricardo.ch/de/a/..." },
  { id: "picclick", label: "PicClick", pattern: /(^|\.)picclick\.[a-z.]+$/i, hint: "picclick.de/...-123456789.html" }
];

const detectGrabSiteId = (value) => {
  let host = "";
  try { host = new URL(String(value || "").trim()).hostname; } catch (_) { return ""; }
  return (GRAB_SITES.find((site) => site.pattern.test(host)) || {}).id || "";
};
const PUBLISH_IMAGE_ALLOWED_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/gif"]);
const PUBLISH_IMAGE_ALLOWED_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif"]);

const looksLikeEbayUrl = (value) => EBAY_ITEM_URL_PATTERN.test(String(value || "").trim());

const detectImageExtension = (contentType = "", url = "") => {
  const mime = String(contentType || "").toLowerCase();
  if (mime.includes("jpeg") || mime.includes("jpg")) return ".jpg";
  if (mime.includes("png")) return ".png";
  if (mime.includes("webp")) return ".webp";
  if (mime.includes("gif")) return ".gif";
  if (mime.includes("avif")) return ".avif";
  const cleanUrl = String(url || "").split("?")[0];
  const extMatch = cleanUrl.match(/\.(jpg|jpeg|png|webp|gif|avif)$/i);
  if (!extMatch) return ".jpg";
  const ext = String(extMatch[1] || "").toLowerCase();
  return ext === "jpeg" ? ".jpg" : `.${ext}`;
};

const normalizeEbayImportImageIdentity = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    const parsed = new URL(raw);
    parsed.hash = "";
    const normalizedPath = String(parsed.pathname || "")
      .replace(/\/thumbs\/images\//i, "/images/")
      .replace(/s-l\d+(?:\.[a-z0-9]+)?$/i, "s-lx")
      .replace(/\.(?:jpe?g|png|webp|gif|bmp|avif)$/i, "");
    return `${String(parsed.hostname || "").toLowerCase()}${normalizedPath}`;
  } catch (error) {
    return raw;
  }
};

const dedupeEbayImportImageUrls = (urls = []) => {
  const result = [];
  const seen = new Set();
  (Array.isArray(urls) ? urls : []).forEach((entry) => {
    const url = String(entry || "").trim();
    if (!url) return;
    const identity = normalizeEbayImportImageIdentity(url) || url;
    if (seen.has(identity)) return;
    seen.add(identity);
    result.push(url);
  });
  return result;
};

const getFileExtension = (name = "") => {
  const match = String(name || "").toLowerCase().match(/\.([a-z0-9]+)$/i);
  return match ? String(match[1] || "").toLowerCase() : "";
};

const shouldConvertImageToJpeg = (file) => {
  const mime = String(file?.type || "").toLowerCase();
  const ext = getFileExtension(file?.name || "");
  if (mime && PUBLISH_IMAGE_ALLOWED_TYPES.has(mime)) return false;
  if (ext && PUBLISH_IMAGE_ALLOWED_EXTENSIONS.has(ext)) return false;
  return true;
};

const normalizePriceInput = (value) => {
  const raw = String(value || "");
  const cleaned = raw.replace(/[^\d.,]/g, "");
  const firstSeparatorIndex = cleaned.search(/[.,]/);
  if (firstSeparatorIndex < 0) return cleaned;
  const integerPart = cleaned.slice(0, firstSeparatorIndex).replace(/[^\d]/g, "");
  const separator = cleaned[firstSeparatorIndex] || ".";
  const decimalPart = cleaned.slice(firstSeparatorIndex + 1).replace(/[^\d]/g, "").slice(0, 2);
  if (!integerPart && !decimalPart) return "";
  return decimalPart ? `${integerPart}${separator}${decimalPart}` : `${integerPart}${separator}`;
};

const loadImageFromBlob = (blob) => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(blob);
  const image = new Image();
  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error("decode-failed"));
  };
  image.src = objectUrl;
});

const dataUrlToJpegBlob = (dataUrl) => {
  const raw = String(dataUrl || "");
  if (!raw.startsWith("data:image/jpeg")) return null;
  const commaIndex = raw.indexOf(",");
  if (commaIndex < 0) return null;
  const base64 = raw.slice(commaIndex + 1);
  if (!base64) return null;
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return new Blob([bytes], { type: "image/jpeg" });
  } catch (error) {
    return null;
  }
};

const decodeImageDrawable = async (file) => {
  try {
    const image = await loadImageFromBlob(file);
    return {
      drawable: image,
      width: Number(image.naturalWidth || image.width || 0),
      height: Number(image.naturalHeight || image.height || 0),
      release: () => {}
    };
  } catch (decodeError) {
    if (typeof createImageBitmap !== "function") throw decodeError;
    const bitmap = await createImageBitmap(file);
    return {
      drawable: bitmap,
      width: Number(bitmap.width || 0),
      height: Number(bitmap.height || 0),
      release: () => {
        if (typeof bitmap.close === "function") {
          try {
            bitmap.close();
          } catch (error) {
            // ignore release errors
          }
        }
      }
    };
  }
};

const convertImageFileToJpeg = async (file) => {
  if (!shouldConvertImageToJpeg(file)) {
    return { file, converted: false };
  }
  const decoded = await decodeImageDrawable(file);
  const width = Number(decoded.width || 0);
  const height = Number(decoded.height || 0);
  if (!width || !height) {
    decoded.release();
    throw new Error("invalid-image-size");
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("canvas-unavailable");
  }

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(decoded.drawable, 0, 0, width, height);
  decoded.release();

  let jpegBlob = await new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
  });
  if (!jpegBlob) {
    const jpegDataUrl = canvas.toDataURL("image/jpeg", 0.92);
    jpegBlob = dataUrlToJpegBlob(jpegDataUrl);
  }
  if (!jpegBlob) {
    throw new Error("jpeg-conversion-failed");
  }

  const originalName = String(file?.name || "image");
  const baseName = originalName.replace(/\.[^.]+$/, "") || "image";
  const convertedFile = new File([jpegBlob], `${baseName}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now()
  });
  return {
    file: convertedFile,
    converted: true,
    sourceName: originalName,
    targetName: convertedFile.name
  };
};

const prepareImageFilesForPublish = async (files = [], { onProgress } = {}) => {
  const accepted = [];
  const converted = [];
  const failed = [];

  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];
    if (typeof onProgress === "function") {
      try {
        onProgress({ index: index + 1, total: files.length, fileName: file?.name || "" });
      } catch (error) {
        // ignore progress callback errors
      }
    }
    try {
      const result = await convertImageFileToJpeg(file);
      // Deliberately not downscaled here. Listing photos are the seller's own
      // product shots, publishing was never the slow path, and the shrink that
      // helps chat attachments only costs quality on an ad.
      accepted.push(result.file);
      if (result.converted) {
        converted.push({
          sourceName: result.sourceName || file?.name || "",
          targetName: result.targetName || result.file?.name || ""
        });
      }
    } catch (error) {
      failed.push({
        name: String(file?.name || "image"),
        reason: String(error?.message || "conversion-failed")
      });
    }
  }

  return { files: accepted, converted, failed };
};

// Names the reason rather than guessing at it. The old wording told the seller to
// use JPG, which sent them in circles the one time this fired on a batch of JPGs:
// the cause was a broken build, not the files. JPG, PNG and GIF are passed through
// untouched, so a rejection here is always either an undecodable file or a format
// the browser cannot convert.
const describeFailedImages = (failed = []) => {
  const names = failed.slice(0, 4).map((item) => item.name).join(", ");
  const more = failed.length > 4 ? tf` и ещё ${failed.length - 4}` : "";
  const reasons = [...new Set(failed.map((item) => String(item?.reason || "").trim()).filter(Boolean))];
  const why = reasons.length === 1 ? tf` Причина: ${reasons[0]}` : "";
  return tf`Не удалось подготовить фото: ${names}${more}.${why}`;
};

const computeFastFingerprint = (file) => {
  if (!file || typeof file.slice !== "function") return Promise.resolve("");
  const maxSlice = 64 * 1024;
  const readSlice = async (blob) => new Uint8Array(await blob.arrayBuffer());
  const foldBytes = (seed, bytes) => {
    let hash = seed >>> 0;
    for (let index = 0; index < bytes.length; index += 1) {
      hash ^= bytes[index];
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  };
  return (async () => {
    const head = await readSlice(file.slice(0, maxSlice));
    const tailStart = Math.max(0, Number(file.size || 0) - maxSlice);
    const tail = tailStart > 0 ? await readSlice(file.slice(tailStart)) : new Uint8Array();
    let hash = 2166136261;
    hash = foldBytes(hash, head);
    hash = foldBytes(hash, tail);
    hash ^= Number(file.size || 0) >>> 0;
    hash = Math.imul(hash, 16777619) >>> 0;
    const type = String(file.type || "").toLowerCase();
    for (let i = 0; i < type.length; i += 1) {
      hash ^= type.charCodeAt(i);
      hash = Math.imul(hash, 16777619) >>> 0;
    }
    return `${Number(file.size || 0)}:${hash.toString(16).padStart(8, "0")}`;
  })().catch(() => "");
};

const dedupeImageFilesByFingerprint = async (files = []) => {
  const uniqueFiles = [];
  const duplicateNames = [];
  const seen = new Set();
  for (const file of Array.isArray(files) ? files : []) {
    const fingerprint = await computeFastFingerprint(file);
    const key = fingerprint || `${String(file?.name || "")}:${Number(file?.size || 0)}`;
    if (seen.has(key)) {
      duplicateNames.push(String(file?.name || ""));
      continue;
    }
    seen.add(key);
    uniqueFiles.push(file);
  }
  return { files: uniqueFiles, duplicateNames };
};

// Дерево категорий, уже раскрытое в этой вкладке браузера. Подкатегории
// приходят по требованию и раньше жили только внутри окна — после закрытия
// выбранную ветку было не из чего восстановить. Отсюда окно и начинает.
let sessionCategoryTree = [];

// Новый PLZ — прежний населённый пункт к нему не относится.
const withPostalCode = (prev, code) => (
  String(prev?.postalCode || "").trim() === String(code || "").trim()
    ? { ...prev, postalCode: code }
    : { ...prev, postalCode: code, locationId: "", locationName: "" }
);

const AdModal = ({
  isOpen,
  dialogState = "open",
  isPhoneView = false,
  onClose,
  onSubmit,
  onSaveDraft,
  publishing,
  accounts,
  categories,
  extraFields,
  extraFieldValues,
  setExtraFieldValues,
  loadingExtraFields,
  extraFieldsError,
  newAd,
  setNewAd,
  adImages,
  setAdImages,
  draftImageUrls = [],
  setDraftImageUrls,
  photoOrder = [],
  setPhotoOrder
}) => {
  if (!isOpen) return null;

  // Язык рубрик и параметров (настройка «Язык категорий»): меняется только
  // показ, в объявление уходят прежние номера категорий и коды значений.
  const categoryLangStamp = useCategoryLang();
  const [categoryPath, setCategoryPath] = useState([]);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [localCategories, setLocalCategories] = useState(() => sessionCategoryTree);
  const [didRestorePath, setDidRestorePath] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  // Перестановка фото — как в окне правки: левая кнопка мыши (со сдвига на 5 px)
  // и кнопки на фото. Номер — позиция в общей сетке (снимки черновика и свои).
  const [photoDragActive, setPhotoDragActive] = useState(false);
  // Фото, открытое в редакторе (components/PhotoEditor.jsx): плитка сетки.
  const [photoEditorItem, setPhotoEditorItem] = useState(null);
  const [photoDragIndex, setPhotoDragIndex] = useState(null);
  const [loadingPlz, setLoadingPlz] = useState(false);
  const [loadingProxyPlz, setLoadingProxyPlz] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const previewPresence = useModalPresence(showPreview);
  const [categoryChildrenError, setCategoryChildrenError] = useState("");
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [ebayUrl, setEbayUrl] = useState("");
  const [grabSiteId, setGrabSiteId] = useState("ebay");
  const [ebayImporting, setEbayImporting] = useState(false);
  const [ebayImportStatus, setEbayImportStatus] = useState("");
  const [ebayImportError, setEbayImportError] = useState("");
  const [categoryQuickQuery, setCategoryQuickQuery] = useState("");
  const [categoryQuickOpen, setCategoryQuickOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  // Подсказка категории по названию: та же модель, что отвечает в сообщениях.
  // forTitle хранит название, под которое подсказка получена, — по нему видно,
  // что название с тех пор изменилось и подсказку стоит обновить.
  const [categorySuggest, setCategorySuggest] = useState({
    status: "idle", items: [], error: "", forTitle: ""
  });
  // Уникализация текста: та же модель переводит на немецкий и переписывает
  // название с описанием другими словами. backup — что стояло до нажатия:
  // переписанное можно вернуть одним нажатием.
  const [rewriteByField, setRewriteByField] = useState({
    title: { status: "idle", error: "", backup: null },
    description: { status: "idle", error: "", backup: null }
  });
  // Объявление по фото: модель смотрит снимки и пишет название, описание,
  // подбирает категорию, выбирает обложку и отмечает, что с фото не так.
  const [photoDescribe, setPhotoDescribe] = useState({ status: "idle", error: "", summary: "", notes: {} });
  // Escape: сначала раскрытый список или предпросмотр, потом само окно.
  const escapeActive = isOpen && dialogState !== "closed";
  useEscapeLayer(escapeActive, () => onClose());
  useEscapeLayer(escapeActive && accountDropdownOpen, () => setAccountDropdownOpen(false));
  useEscapeLayer(escapeActive && categoryPickerOpen, () => setCategoryPickerOpen(false));
  useEscapeLayer(escapeActive && categoryQuickOpen, () => setCategoryQuickOpen(false));
  useEscapeLayer(escapeActive && showPreview, () => setShowPreview(false));
  const aiPrefs = useAiPrefs();
  const categoryListRef = useRef(null);
  const imageInputRef = useRef(null);
  const accountDropdownRef = useRef(null);
  const pendingChildrenRef = useRef(new Map());
  const prefetchRootRef = useRef("");
  const prefetchInFlightRef = useRef(false);
  const prefetchedTargetsRef = useRef(new Set());
  const prefetchTokenRef = useRef(0);
  const photoDragRef = useRef({ active: false, pending: false, index: -1, startX: 0, startY: 0 });
  const lastAutofillAccountIdRef = useRef("");

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    border: "1px solid rgba(141,147,163,0.28)",
    borderRadius: "18px",
    background: "rgba(8,9,12,0.55)",
    fontSize: "14px",
    color: "#e2e4e9",
    transition: "all 0.2s ease",
    outline: "none",
    boxShadow: "inset 0 0 0 1px rgba(23,25,33,0.68)"
  };

  // Section heading (design 2a): 12.5px / 700 / var(--accent-100), no colon, the field sits
  // directly underneath.
  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontWeight: 700,
    color: A[100],
    fontSize: "12.5px",
    letterSpacing: "0.01em"
  };

  // Header title counter (design 2a): grey under 10 chars, amber from 60, red at
  // the 65-char limit.
  const titleLength = (newAd.title || "").length;
  const titleProgressColor = titleLength >= 65
    ? "#f87171"
    : (titleLength >= 60 ? "#fde68a" : (titleLength < 10 ? "rgba(141,147,163,0.55)" : A[500]));

  // Section heading (no colon, no card wrapper) — replaces the old labelStyle
  // inside the two columns.
  const sectionTitleStyle = {
    fontSize: "12.5px",
    fontWeight: 700,
    color: A[100],
    letterSpacing: "0.01em"
  };

  // The boxed field groups are removed in design 2a — sections now sit directly
  // on the column background. Kept as a no-op wrapper so the markup is unchanged.
  const sectionCardStyle = {
    borderRadius: 0,
    border: "none",
    background: "transparent",
    padding: 0
  };

  const inputFocusColor = aa(300, 0.58);
  const inputBlurColor = "rgba(141,147,163,0.28)";

  const ghostButtonStyle = {
    borderRadius: "16px",
    border: "1px solid rgba(141,147,163,0.3)",
    background: `${sa(0.62)}`,
    color: "#c6c9d1",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontSize: "13px",
    fontWeight: "700"
  };

  const accentButtonStyle = {
    borderRadius: "16px",
    border: `1px solid ${aa(300, 0.38)}`,
    background: `linear-gradient(135deg, ${aa(600, 0.92)}, ${aa(600, 0.85)})`,
    color: onAccent(A[50]),
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontSize: "13px",
    fontWeight: "700"
  };
  const applyHoverStyles = (event, styles) => {
    if (!event?.currentTarget || !styles) return;
    Object.assign(event.currentTarget.style, styles);
  };
  const actionHoverEnter = {
    background: `linear-gradient(135deg, ${aa(700, 0.38)}, ${aa(600, 0.26)})`,
    borderColor: aa(300, 0.56),
    boxShadow: `0 14px 28px ${aa(900, 0.24)}`,
    transform: "translateY(-2px)"
  };
  const ghostHoverEnter = {
    ...actionHoverEnter
  };
  const ghostHoverLeave = {
    background: ghostButtonStyle.background,
    borderColor: "rgba(141,147,163,0.3)",
    boxShadow: "none",
    transform: "translateY(0)"
  };
  const accentHoverEnter = {
    ...actionHoverEnter,
    filter: "brightness(1.06)"
  };
  const accentHoverLeave = {
    background: accentButtonStyle.background,
    borderColor: aa(300, 0.38),
    boxShadow: "none",
    transform: "translateY(0)",
    filter: "none"
  };
  // Зависимые параметры категории. Kleinanzeigen требует такое поле только при
  // определённом значении другого: например в Haustiere → Zubehör выбор
  // «Art = Pferde» добавляет обязательное «Art des Zubehörs» со своим списком,
  // а у остальных значений этого поля нет вовсе. Пока панель его не показывала,
  // публикация с Pferde отклонялась (reject.empty на zubehoer.type_s).
  const resolveExtraFieldOptions = (field) => {
    if (!field?.dependsOn) return Array.isArray(field?.options) ? field.options : [];
    const parentValue = String(extraFieldValues?.[field.dependsOn] || "").trim();
    if (!parentValue) return [];
    const byParent = field.optionsByParent && typeof field.optionsByParent === "object"
      ? field.optionsByParent
      : {};
    return Array.isArray(byParent[parentValue]) ? byParent[parentValue] : [];
  };
  // Зависимое поле показывается, только когда у выбранного значения родителя
  // действительно есть варианты; обязательным оно считается там же.
  const visibleExtraFields = (Array.isArray(extraFields) ? extraFields : [])
    .filter((field) => !field?.dependsOn || resolveExtraFieldOptions(field).length > 0);

  const missingRequiredExtraFields = visibleExtraFields
    .filter((field) => field?.required)
    .filter((field) => {
      const key = field.name || field.label;
      const value = key ? extraFieldValues?.[key] : "";
      return !String(value ?? "").trim();
    });
  const hasSelectedCategory = Boolean(newAd?.categoryId || newAd?.categoryUrl);
  const submitBlockedByFields =
    hasSelectedCategory && (loadingExtraFields || Boolean(extraFieldsError) || missingRequiredExtraFields.length > 0);

  // Publishing is never blocked (design 2a §75): clicking with unfilled required
  // parameters highlights them and scrolls the column to the first one instead.
  const bodyScrollRef = useRef(null);
  const [highlightMissing, setHighlightMissing] = useState(false);
  const handlePublishClick = () => {
    if (submitBlockedByFields) {
      setHighlightMissing(true);
      const node = bodyScrollRef.current;
      const target = node?.querySelector?.("[data-missing-required='1']");
      if (node && target) {
        if (isPhoneLayout) target.scrollIntoView({ behavior: "smooth", block: "center" });
        else node.scrollTop = Math.max(0, target.offsetTop - node.offsetTop - 12);
      }
      return;
    }
    setHighlightMissing(false);
    onSubmit?.();
  };
  useEffect(() => {
    if (highlightMissing && !submitBlockedByFields) setHighlightMissing(false);
  }, [highlightMissing, submitBlockedByFields]);

  // Footer status: mirror everything handleCreateAd actually requires, not just
  // the category parameters — otherwise an empty form still read "Готово".
  const photoCount = (Array.isArray(adImages) ? adImages.length : 0)
    + (Array.isArray(draftImageUrls) ? draftImageUrls.filter((item) => String(item || "").trim()).length : 0);
  const missingBasics = [
    !newAd?.accountId && t("аккаунт"),
    !String(newAd?.title || "").trim() && t("название"),
    !String(newAd?.description || "").trim() && t("описание"),
    !String(newAd?.price || "").trim() && t("цену"),
    !hasSelectedCategory && t("категорию"),
    !photoCount && t("фото")
  ].filter(Boolean);
  const missingRequiredCount = missingBasics.length + missingRequiredExtraFields.length;
  const notReadyToPublish = missingRequiredCount > 0 || loadingExtraFields || Boolean(extraFieldsError);
  const missingRequiredLabel = (() => {
    if (loadingExtraFields) return t("Загружаем параметры категории...");
    if (extraFieldsError) return t("Не удалось загрузить параметры категории");
    if (missingBasics.length) {
      const head = missingBasics.slice(0, 2).join(", ");
      const rest = missingRequiredCount - Math.min(2, missingBasics.length);
      return rest > 0 ? tf`Заполните: ${head} и ещё ${rest}` : tf`Заполните: ${head}`;
    }
    const n = missingRequiredCount;
    const word = (n % 10 === 1 && n % 100 !== 11)
      ? t("обязательный параметр")
      : ((n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) ? t("обязательных параметра") : t("обязательных параметров"));
    return tf`Осталось заполнить ${n} ${word}`;
  })();

  const formatAccountLabel = (account) => {
    const name = account.profileName || account.username || t("Аккаунт");
    const email = account.profileEmail || "";
    return email ? `${name} (${email})` : name;
  };

  const normalizeSatisfactionBadge = (value) => {
    const normalized = String(value || "").trim().toUpperCase();
    if (["TOP", "OK", "NAJA", "MIX"].includes(normalized)) return normalized;
    return "MIX";
  };

  const getAccountProxyLabel = (account) =>
    String(account?.proxy || account?.proxyName || "").trim() || t("Без прокси");

  const selectedAccountMeta = accounts.find((account) => String(account.id) === String(newAd.accountId)) || null;
  const previewAccountName = selectedAccountMeta
    ? String(selectedAccountMeta.profileName || selectedAccountMeta.username || t("Аккаунт")).trim() || t("Аккаунт")
    : t("Аккаунт не выбран");
  const previewAccountEmail = selectedAccountMeta
    ? String(selectedAccountMeta.profileEmail || "").trim()
    : "";
  const isPhoneLayout = Boolean(isPhoneView);
  // Та же защита, что у окна добавления аккаунта: фон под открытой
  // карточкой не прокручивается.
  useEffect(() => {
    if (!isOpen) return undefined;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [isOpen]);
  const overlayRef = useRef(null);
  useModalTouchGuard(overlayRef, isOpen && isPhoneLayout);
  const filledPreviewExtraFields = visibleExtraFields
    .map((field) => {
      const key = field?.name || field?.label;
      const value = key ? String(extraFieldValues?.[key] || "").trim() : "";
      if (!value) return null;
      // В значении — код Kleinanzeigen («ja», «sehr_gut»); показываем подпись
      // выбранного варианта, если она есть.
      const option = resolveExtraFieldOptions(field).find((item) => String(item?.value ?? "") === value);
      return {
        label: field?.label ? catText(String(field.label).trim()) : String(key || t("Параметр")).trim(),
        value: option?.label ? catText(option.label) : value
      };
    })
    .filter(Boolean)
    .slice(0, 10);

  // Название, как его отдаёт Kleinanzeigen, — для поиска: искать можно и
  // по-немецки, когда рубрики показаны на другом языке.
  const getCategoryRawLabel = (category) => {
    if (!category) return "";
    const raw =
      category.name ||
      category.label ||
      category.title ||
      category.categoryName ||
      category.displayName;
    const text = String(raw || "").trim();
    if (text) return text;
    const fallback = category.id || category.url || "";
    return String(fallback);
  };
  const getCategoryLabel = (category) => catText(getCategoryRawLabel(category));

  const isNumericCategoryId = (value) => /^\d+$/.test(String(value || ""));

  const looksLikeCategoryUrl = (value) => /\/c\d+(?:\/|$)/i.test(String(value || ""));

  const isAttributeOptionNode = (node, parent) => {
    if (!node || !parent) return false;
    const parentId = parent.id !== undefined && parent.id !== null ? String(parent.id) : "";
    if (!isNumericCategoryId(parentId)) return false;
    const idValue = node.id !== undefined && node.id !== null ? String(node.id) : "";
    const urlValue = node.url ? String(node.url) : "";
    const nameValue = String(node.name || node.label || "").trim().toLowerCase();
    const normalizedId = idValue.trim().toLowerCase();
    if (isNumericCategoryId(normalizedId)) return false;
    if (looksLikeCategoryUrl(urlValue)) return false;
    if (Array.isArray(node.children) && node.children.length > 0) return false;
    if (!normalizedId || !nameValue) return false;
    // Keep real subcategories with slug-like ids (e.g. "drucker_scanner").
    // Filter only very small option-like values that are likely field options.
    const optionLikeValues = new Set(["ja", "nein", "yes", "no", "new", "used", "other"]);
    return optionLikeValues.has(normalizedId) || optionLikeValues.has(nameValue);
  };

  const filterCategoryChildren = (nodes, parent) =>
    (Array.isArray(nodes) ? nodes : []).filter((node) => !isAttributeOptionNode(node, parent));

  const getCategoryValue = (category) => {
    if (!category) return "";
    const id = category.id !== undefined && category.id !== null ? String(category.id) : "";
    const url = category.url ? String(category.url) : "";
    if (id && isNumericCategoryId(id)) return id;
    if (url) return url;
    return id;
  };

  const getNodeKey = (node) => {
    if (!node) return "";
    const value = getCategoryValue(node);
    if (value) return `value:${value}`;
    if (node.name) return `name:${String(node.name)}`;
    return "";
  };

  const mergeCategoryTrees = (prevNodes, nextNodes) => {
    if (!Array.isArray(nextNodes) || !nextNodes.length) {
      return Array.isArray(prevNodes) ? prevNodes : [];
    }
    const prev = Array.isArray(prevNodes) ? prevNodes : [];
    const prevMap = new Map();
    for (const node of prev) {
      const key = getNodeKey(node);
      if (key) prevMap.set(key, node);
    }
    const used = new Set();
    const merged = nextNodes.map((node) => {
      const key = getNodeKey(node);
      if (key) used.add(key);
      const prevNode = key ? prevMap.get(key) : null;
      const nextChildren = Array.isArray(node.children) ? node.children : [];
      const prevChildren = prevNode?.children || [];
      const mergedChildren = nextChildren.length
        ? mergeCategoryTrees(prevChildren, nextChildren)
        : prevChildren;
      const childrenLoaded = Boolean(node?.childrenLoaded || prevNode?.childrenLoaded);
      return {
        ...(prevNode || {}),
        ...node,
        children: mergedChildren,
        childrenLoaded
      };
    });
    for (const [key, node] of prevMap.entries()) {
      if (!used.has(key)) merged.push(node);
    }
    return merged;
  };

  const buildPathToNode = (nodes, targetValue, path = []) => {
    if (!targetValue || !Array.isArray(nodes)) return [];
    const target = String(targetValue);
    for (const node of nodes) {
      const matches =
        (node.id !== undefined && node.id !== null && String(node.id) === target) ||
        (node.url && String(node.url) === target);
      const nextValue = getCategoryValue(node);
      const nextPath = nextValue ? [...path, nextValue] : path;
      if (matches) return nextPath;
      if (node.children?.length) {
        const childPath = buildPathToNode(node.children, targetValue, nextPath);
        if (childPath.length) return childPath;
      }
    }
    return [];
  };

  const findNode = (nodes, value) => {
    if (!value) return null;
    for (const node of nodes) {
      if (String(node.id) === String(value) || String(node.url) === String(value)) return node;
      if (node.children?.length) {
        const found = findNode(node.children, value);
        if (found) return found;
      }
    }
    return null;
  };

  const getBreadcrumbForPath = (path) => {
    const breadcrumb = [];
    let current = localCategories;
    for (const pathItem of path) {
      const found = current.find((cat) =>
        (cat.id && String(cat.id) === String(pathItem)) ||
        (cat.url && String(cat.url) === String(pathItem))
      );
      if (!found) break;
      breadcrumb.push(found);
      current = filterCategoryChildren(found.children || [], found);
    }
    return breadcrumb;
  };

  const resolveCategorySelection = (path) => {
    const breadcrumb = getBreadcrumbForPath(path);
    const selectedNode = breadcrumb.length ? breadcrumb[breadcrumb.length - 1] : null;
    const numericNode = [...breadcrumb].reverse().find((node) => /^\d+$/.test(String(node?.id || "")));
    return {
      selectedNode,
      numericNode: numericNode || selectedNode
    };
  };

  const buildQuickCategoryOptions = (nodes, path = [], parentNode = null) => {
    const source = filterCategoryChildren(nodes, parentNode);
    const list = [];
    for (const node of source) {
      const value = getCategoryValue(node);
      const label = getCategoryLabel(node);
      if (!value || !label) continue;
      const rawLabel = getCategoryRawLabel(node);
      const nextPath = [...path, { value: String(value), label, rawLabel }];
      const fullPathLabel = nextPath.map((item) => item.label).join(" / ");
      const rawPathLabel = nextPath.map((item) => item.rawLabel).join(" / ");
      list.push({
        value: String(value),
        label,
        pathValues: nextPath.map((item) => item.value),
        fullPathLabel,
        searchText: rawPathLabel === fullPathLabel
          ? fullPathLabel.toLowerCase()
          : `${fullPathLabel}\n${rawPathLabel}`.toLowerCase()
      });
      if (Array.isArray(node.children) && node.children.length) {
        list.push(...buildQuickCategoryOptions(node.children, nextPath, node));
      }
    }
    return list;
  };

  function syncQuickCategoryQueryFromPath(path) {
    const labels = getBreadcrumbForPath(Array.isArray(path) ? path : [])
      .map((item) => getCategoryLabel(item))
      .filter(Boolean);
    setCategoryQuickQuery(labels.join(" / "));
  }

  useEffect(() => {
    setLocalCategories((prev) => mergeCategoryTrees(prev, categories || []));
  }, [categories]);

  useEffect(() => {
    sessionCategoryTree = localCategories;
  }, [localCategories]);

  useEffect(() => {
    if (isOpen) {
      setCategoryPath([]);
      setDidRestorePath(false);
      setCategoryChildrenError("");
      setAccountDropdownOpen(false);
      setEbayImporting(false);
      setEbayImportStatus("");
      setEbayImportError("");
      setCategoryQuickQuery("");
      setCategoryQuickOpen(false);
      // Категория в newAd не сбрасывается: после закрытия крестиком или
      // Escape она остаётся, как название и описание, и ветка ниже
      // восстанавливается по сохранённому пути. Чистят форму только
      // публикация и сохранение черновика.
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !accountDropdownOpen) return undefined;
    const handleClickOutside = (event) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target)) {
        setAccountDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, accountDropdownOpen]);

  useEffect(() => {
    if (!adImages || adImages.length === 0) {
      setImagePreviews([]);
      return undefined;
    }

    const previews = adImages.map((file) => ({
      file,
      url: URL.createObjectURL(file)
    }));
    setImagePreviews(previews);

    return () => {
      previews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [adImages]);

  const stopPhotoDrag = () => {
    const drag = photoDragRef.current;
    if (!drag.active && !drag.pending) return;
    photoDragRef.current = { active: false, pending: false, index: -1, startX: 0, startY: 0 };
    setPhotoDragActive(false);
    setPhotoDragIndex(null);
  };

  useEffect(() => {
    const handlePointerMove = (event) => {
      const drag = photoDragRef.current;
      if (!drag.active && !drag.pending) return;
      if (!drag.active) {
        if (Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < 5) return;
        photoDragRef.current = { ...drag, active: true, pending: false };
        setPhotoDragActive(true);
        setPhotoDragIndex(drag.index);
      }
      const hovered = document.elementFromPoint(event.clientX, event.clientY);
      const tile = hovered && typeof hovered.closest === "function" ? hovered.closest("[data-photo-index]") : null;
      if (tile) movePhotoDragTo(Number(tile.getAttribute("data-photo-index")));
    };
    const stop = () => stopPhotoDrag();
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    window.addEventListener("blur", stop);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      window.removeEventListener("blur", stop);
    };
  }, []);

  // Выбранные и перетащенные фото добавляются к уже стоящим, а не заменяют
  // их. Раньше каждый выбор файлов ставил в объявление только новые: к фото,
  // забранным по ссылке, нельзя было добавить своё — они пропадали, оставалось
  // одно добавленное. Повтор того же снимка отбрасывается по отпечатку файла,
  // а сверх общего лимита (вместе с фото черновика) лишнее не добавляется.
  const adImagesRef = useRef(adImages);
  adImagesRef.current = adImages;
  const appendAdImageFiles = async (selected) => {
    const prepared = await prepareImageFilesForPublish(selected);
    if (prepared.failed.length) alert(describeFailedImages(prepared.failed));
    if (!prepared.files.length) return;
    const current = Array.isArray(adImagesRef.current) ? adImagesRef.current : [];
    const { files: unique, duplicateNames } = await dedupeImageFilesByFingerprint([...current, ...prepared.files]);
    const draftCount = Array.isArray(draftImageUrls)
      ? draftImageUrls.filter((item) => String(item || "").trim()).length
      : 0;
    const room = Math.max(0, AD_MAX_IMAGES - draftCount);
    const next = unique.slice(0, room);
    const added = next.length - current.length;
    const skippedByLimit = unique.length - next.length;
    if (added > 0) setAdImages(next);
    if (skippedByLimit > 0) {
      alert(tf`Можно не больше ${AD_MAX_IMAGES} фото в объявлении — не добавлено: ${skippedByLimit}.`);
    } else if (added <= 0 && duplicateNames.length) {
      alert(t("Эти фото уже добавлены."));
    }
  };

  const handleImageInputChange = async (event) => {
    const selected = Array.from(event?.target?.files || []).filter((file) =>
      String(file?.type || "").toLowerCase().startsWith("image/")
    );
    if (event?.target) event.target.value = "";
    if (!selected.length) return;
    await appendAdImageFiles(selected);
  };

  const handleImageDrop = async (event) => {
    event.preventDefault();
    const selected = Array.from(event?.dataTransfer?.files || []).filter((file) =>
      String(file?.type || "").toLowerCase().startsWith("image/")
    );
    if (!selected.length) return;
    await appendAdImageFiles(selected);
  };

  const fetchEbayImageFile = async (url, index) => {
    const response = await apiFetch(`/api/ads/ebay-image?url=${encodeURIComponent(url)}`, {
      timeoutMs: 45000,
      retry: true
    });
    if (!response.ok) {
      let details = "";
      try {
        details = (await response.text()).trim();
      } catch (error) {
        details = "";
      }
      throw new Error(details || `HTTP ${response.status}`);
    }
    const blob = await response.blob();
    const contentType = response.headers.get("content-type") || blob.type || "image/jpeg";
    const ext = detectImageExtension(contentType, url);
    const name = `ebay-${String(index + 1).padStart(2, "0")}${ext}`;
    return new File([blob], name, { type: contentType, lastModified: Date.now() });
  };

  const handleEbayGrab = async () => {
    const sourceUrl = String(ebayUrl || "").trim();
    const site = GRAB_SITES.find((item) => item.id === grabSiteId) || GRAB_SITES[0];
    if (!sourceUrl) {
      setEbayImportError(tf`Вставьте ссылку на товар ${site.label}.`);
      setEbayImportStatus("");
      return;
    }
    // Площадку определяем по самой ссылке: если она с другой, чем выбрана,
    // переключаемся молча — так надёжнее, чем просить исправить выбор.
    const detected = detectGrabSiteId(sourceUrl);
    const effective = detected
      ? (GRAB_SITES.find((item) => item.id === detected) || site)
      : site;
    if (detected && detected !== grabSiteId) setGrabSiteId(detected);
    if (!detected) {
      setEbayImportError(tf`Ссылка должна вести на страницу товара ${site.label}.`);
      setEbayImportStatus("");
      return;
    }

    setEbayImporting(true);
    setEbayImportError("");
    setEbayImportStatus(tf`Забираю данные со страницы ${effective.label}...`);

    try {
      const isEbay = effective.id === "ebay";
      const result = await apiFetchJson(isEbay ? "/api/ads/ebay-grab" : "/api/ads/grab", {
        method: "POST",
        // Больше, чем срок у сервера: иначе панель сдаётся раньше, чем он успеет
        // ответить внятной причиной, и вместо неё показывает свой таймаут. У
        // eBay путей больше — прямой запрос, отпечаток Chrome, прокси, браузер,
        // который ещё и ждёт, пока его пропустит проверка, — и срок длиннее.
        timeoutMs: isEbay ? 150000 : 90000,
        retry: false,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: sourceUrl })
      });
      // У eBay ответ вложен в item, у остальных площадок поля лежат сразу.
      const item = isEbay ? (result?.item || {}) : (result || {});
      const nextTitle = String(item?.title || "").trim();
      const nextDescription = String(item?.description || "").trim();
      const imageUrls = Array.isArray(item?.imageUrls || item?.images)
        ? (item.imageUrls || item.images).map((entry) => String(entry || "").trim()).filter(Boolean)
        : [];
      const dedupedImageUrls = dedupeEbayImportImageUrls(imageUrls);

      if (!nextTitle && !nextDescription && dedupedImageUrls.length === 0) {
        throw new Error(t("Граббер не нашел данных на странице."));
      }

      if (nextTitle || nextDescription) {
        setNewAd((prev) => ({
          ...prev,
          title: (nextTitle || prev.title || "").slice(0, 65),
          description: (nextDescription || prev.description || "").slice(0, 4000)
        }));
      }

      const files = [];
      const limitedUrls = dedupedImageUrls.slice(0, EBAY_IMPORT_MAX_IMAGES);
      for (let index = 0; index < limitedUrls.length; index += 1) {
        const imageUrl = limitedUrls[index];
        setEbayImportStatus(tf`Загружаю фото ${index + 1}/${limitedUrls.length}...`);
        try {
          const file = await fetchEbayImageFile(imageUrl, index);
          if (file) files.push(file);
        } catch (error) {
          adModalDebugWarn("[eBay import] image fetch failed", imageUrl, error?.message || error);
        }
      }

      const dedupedFetched = await dedupeImageFilesByFingerprint(files);
      const duplicateFetchedCount = Math.max(0, files.length - dedupedFetched.files.length);
      if (duplicateFetchedCount > 0) {
        adModalDebugLog(`[eBay import] dropped duplicate files: ${duplicateFetchedCount}`);
      }

      let preparedFiles = dedupedFetched.files;
      let convertedImages = [];
      let failedImages = [];
      if (dedupedFetched.files.length > 0) {
        const prepared = await prepareImageFilesForPublish(dedupedFetched.files, {
          onProgress: ({ index, total }) => {
            setEbayImportStatus(tf`Подготавливаю фото ${index}/${total}...`);
          }
        });
        preparedFiles = prepared.files;
        convertedImages = prepared.converted;
        failedImages = prepared.failed;
        if (preparedFiles.length > 0) {
          setAdImages(preparedFiles);
        }
      }

      const stats = [];
      if (nextTitle) stats.push(t("название"));
      if (nextDescription) stats.push(t("описание"));
      if (preparedFiles.length > 0) stats.push(tf`фото: ${preparedFiles.length}`);
      if (duplicateFetchedCount > 0) stats.push(tf`дубликаты убраны: ${duplicateFetchedCount}`);
      if (convertedImages.length > 0) stats.push(tf`конвертировано в JPG: ${convertedImages.length}`);
      setEbayImportStatus(stats.length
        ? tf`Импорт завершен: ${stats.join(", ")}.`
        : t("Импорт завершен."));
      if (!preparedFiles.length && dedupedImageUrls.length) {
        setEbayImportError(t("Данные объявления загружены, но фото скачать/подготовить не удалось."));
      } else if (failedImages.length) {
        const failedNames = failedImages.slice(0, 4).map((item) => item.name).join(", ");
        setEbayImportError(tf`Часть фото пропущена: ${failedNames}.`);
      }
    } catch (error) {
      // Сообщения сервера — по-русски; t() переводит те, что есть в словаре.
      setEbayImportError(t(error?.message) || t("Не удалось загрузить объявление."));
      setEbayImportStatus("");
    } finally {
      setEbayImporting(false);
    }
  };

  // Отредактированное фото встаёт на место прежнего — та же позиция в сетке,
  // та же обложка. Снимок черновика при этом становится своим файлом: на
  // сервере лежит прежний, а уйти должен новый.
  const replaceEditedPhoto = (item, editedFile) => {
    if (!item || !editedFile) return;
    if (item.source === "local") {
      const original = (Array.isArray(adImagesRef.current) ? adImagesRef.current : [])[item.localIndex];
      if (!original) return;
      setAdImages((prev) => (Array.isArray(prev) ? prev : []).map((file) => (file === original ? editedFile : file)));
      if (typeof setPhotoOrder === "function") {
        setPhotoOrder((prev) => (Array.isArray(prev) ? prev : []).map((token) => (
          token?.kind === "file" && token.file === original ? { kind: "file", file: editedFile } : token
        )));
      }
      return;
    }
    const url = String(item.url || "").trim();
    if (!url || typeof setDraftImageUrls !== "function") return;
    if (typeof setPhotoOrder === "function") {
      setPhotoOrder((prev) => buildPhotoSequence(prev, draftImageUrlsRef.current, adImagesRef.current).map((token) => (
        token.kind === "draft" && token.url === url ? { kind: "file", file: editedFile } : token
      )));
    }
    setDraftImageUrls((prev) => (Array.isArray(prev) ? prev : []).filter((entry) => String(entry || "").trim() !== url));
    setAdImages((prev) => [...(Array.isArray(prev) ? prev : []), editedFile]);
  };

  const removeAdImage = (indexToRemove) => {
    stopPhotoDrag();
    setAdImages((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      return list.filter((_, idx) => idx !== indexToRemove);
    });
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const removeDraftImage = (indexToRemove) => {
    if (typeof setDraftImageUrls !== "function") return;
    setDraftImageUrls((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      return list.filter((_, idx) => idx !== indexToRemove);
    });
  };

  // Общий порядок: черновые снимки и свои файлы (utils/photoOrder). Наборы
  // читаются из ref-ов, чтобы перестановка во время перетаскивания видела
  // последние, а не те, что были при нажатии.
  const draftImageUrlsRef = useRef(draftImageUrls);
  draftImageUrlsRef.current = draftImageUrls;
  const movePhoto = (fromIndex, toIndex) => {
    if (typeof setPhotoOrder !== "function") return;
    setPhotoOrder((prev) => moveInSequence(
      buildPhotoSequence(prev, draftImageUrlsRef.current, adImagesRef.current),
      fromIndex,
      toIndex
    ));
  };

  // Левая кнопка мыши. На сенсорном экране тянуть пальцем значило бы отнять у
  // окна прокрутку — там порядок меняют кнопки на фото.
  const startPhotoDrag = (event, index) => {
    if (event.pointerType && event.pointerType !== "mouse") return;
    if (event.button !== 0) return;
    if (event.target && typeof event.target.closest === "function" && event.target.closest("button")) return;
    event.preventDefault();
    photoDragRef.current = { active: false, pending: true, index, startX: event.clientX, startY: event.clientY };
  };

  const movePhotoDragTo = (targetIndex) => {
    const current = photoDragRef.current;
    if (!current.active) return;
    if (!Number.isInteger(targetIndex) || current.index === targetIndex) return;
    movePhoto(current.index, targetIndex);
    photoDragRef.current = { ...current, index: targetIndex };
    setPhotoDragIndex(targetIndex);
  };

  useEffect(() => {
    if (!isOpen) return;
    // Пока сохранённая ветка не восстановлена, пустой путь окна — не выбор
    // пользователя, и стирать им путь в newAd нельзя.
    if (!didRestorePath && !categoryPath.length) return;
    setNewAd((prev) => {
      const currentPath = Array.isArray(prev.categoryPath) ? prev.categoryPath : [];
      const nextPath = categoryPath;
      if (currentPath.length === nextPath.length && currentPath.every((item, idx) => String(item) === String(nextPath[idx]))) {
        return prev;
      }
      return { ...prev, categoryPath: [...nextPath] };
    });
  }, [isOpen, categoryPath, didRestorePath, setNewAd]);

  // Восстановление выбранной категории при открытии — по сохранённому пути, а
  // не по номеру: у подкатегорий без своего номера он указывает на родителя,
  // и путь вышел бы короче выбранного. Уровень, которого ещё нет в дереве,
  // догружается — эффект повторится, когда дерево пополнится.
  useEffect(() => {
    if (!isOpen) return;
    if (categoryPath.length) return;
    if (didRestorePath) return;
    const matchesStep = (cat, step) =>
      (cat.id !== undefined && cat.id !== null && String(cat.id) === step) ||
      (cat.url && String(cat.url) === step);
    const savedPath = (Array.isArray(newAd?.categoryPath) ? newAd.categoryPath : [])
      .map((item) => String(item || ""))
      .filter(Boolean);
    if (savedPath.length) {
      let level = localCategories;
      let parent = null;
      let complete = true;
      for (const step of savedPath) {
        const node = (Array.isArray(level) ? level : []).find((cat) => matchesStep(cat, step));
        if (!node) {
          complete = false;
          break;
        }
        parent = node;
        level = node.children;
      }
      if (complete) {
        setCategoryPath(savedPath);
        syncQuickCategoryQueryFromPath(savedPath);
        setDidRestorePath(true);
        return;
      }
      const levelLoaded = parent
        ? Boolean(parent.childrenLoaded || (Array.isArray(parent.children) && parent.children.length))
        : localCategories.length > 0;
      if (!levelLoaded) {
        if (parent) requestChildren(parent);
        return;
      }
      // Уровень загружен, а шага в нём нет (категорию переименовали или
      // убрали) — ниже ищем по номеру категории.
    }
    const target = newAd?.categoryId || newAd?.categoryUrl;
    if (!target) return;
    const restoredPath = buildPathToNode(localCategories, target);
    if (restoredPath.length) {
      setCategoryPath(restoredPath);
      syncQuickCategoryQueryFromPath(restoredPath);
      setDidRestorePath(true);
    }
  }, [isOpen, categoryPath.length, didRestorePath, newAd?.categoryId, newAd?.categoryUrl, newAd?.categoryPath, localCategories]);

  useEffect(() => {
    if (!isOpen) return;
    if (!categoryPath.length) return;
    const lastId = categoryPath[categoryPath.length - 1];
    const node = findNode(localCategories, lastId);
    if (node && (!node.children || node.children.length === 0)) {
      requestChildren(node);
    }
  }, [isOpen, newAd.accountId, categoryPath, localCategories]);

  useEffect(() => {
    if (!isOpen) return;
    if (!categoryPath.length) {
      adModalDebugLog("[AdModal] category path empty");
      return;
    }
    const current = getCurrentCategories();
    adModalDebugLog("[AdModal] category path", categoryPath.join(" > "), "current count", current.length);
  }, [isOpen, categoryPath, localCategories]);

  useEffect(() => {
    if (!isOpen) return;
    if (!categoryListRef.current) return;
    categoryListRef.current.scrollTop = 0;
  }, [isOpen, categoryPath]);

  const getCurrentCategories = () => {
    if (categoryPath.length === 0) return localCategories;

    let current = localCategories;
    let parent = null;
    for (const pathItem of categoryPath) {
      const found = current.find((cat) =>
        (cat.id && String(cat.id) === String(pathItem)) ||
        (cat.url && String(cat.url) === String(pathItem))
      );
      if (!found) {
        const fallback = findNode(localCategories, pathItem);
        return filterCategoryChildren(fallback?.children || [], fallback);
      }
      parent = found;
      current = filterCategoryChildren(found.children || [], found);
    }
    return current;
  };

  const getSelectedCategory = () => {
    if (categoryPath.length === 0) return null;
    const lastId = categoryPath[categoryPath.length - 1];
    return findNode(localCategories, lastId);
  };


  const updateNodeChildren = (nodes, targetValue, children) =>
    nodes.map((node) => {
      const matches = String(node.id) === String(targetValue) || String(node.url) === String(targetValue);
      if (matches) {
        return { ...node, children, childrenLoaded: true };
      }
      if (node.children?.length) {
        return { ...node, children: updateNodeChildren(node.children, targetValue, children) };
      }
      return node;
    });

  const requestChildren = async (node) => {
    const rawId = node?.id !== undefined && node?.id !== null ? String(node.id) : "";
    const rawUrl = node?.url ? String(node.url) : "";
    const idValue = rawId && isNumericCategoryId(rawId) ? rawId : "";
    const urlValue = idValue ? "" : (rawUrl || "");
    if (!idValue && !urlValue) return;
    const targetValue = getCategoryValue(node) || idValue || urlValue;
    if (!targetValue) return;
    if (node?.childrenLoaded && Array.isArray(node?.children)) {
      return node.children;
    }

    const pending = pendingChildrenRef.current.get(targetValue);
    if (pending) return pending;

    const task = (async () => {
      const params = new URLSearchParams();
      if (idValue) params.set("id", idValue);
      else params.set("url", urlValue);
      if (newAd?.accountId) params.set("accountId", newAd.accountId);
      try {
        const data = await apiFetchJson(`/api/categories/children?${params.toString()}`, {
          timeoutMs: 90000,
          retry: true,
          allowBaseFallback: true
        });
        const rawChildren = Array.isArray(data?.children) ? data.children : null;
        if (!Array.isArray(rawChildren)) return null;
        const filteredChildren = filterCategoryChildren(rawChildren, node);
        const count = Array.isArray(filteredChildren) ? filteredChildren.length : 0;
        const sample = Array.isArray(filteredChildren)
          ? filteredChildren.slice(0, 5).map((item) => item?.name || item?.id || item?.url)
          : [];
        adModalDebugLog("[AdModal] children response count", count, "target", targetValue, "sample", sample);
        setLocalCategories((prev) => {
          const next = updateNodeChildren(prev, targetValue, filteredChildren);
          const updated = findNode(next, targetValue);
          adModalDebugLog("[AdModal] children applied count", updated?.children?.length || 0, "target", targetValue);
          return next;
        });
        setCategoryChildrenError("");
        return filteredChildren;
      } catch (error) {
        const message = error?.message || t("Ошибка сети");
        adModalDebugError("[AdModal] children request failed", {
          target: targetValue,
          accountId: newAd?.accountId || "",
          message
        });
        setCategoryChildrenError(tf`Не удалось загрузить подкатегории: ${message}`);
        return null;
      }
    })();

    pendingChildrenRef.current.set(targetValue, task);
    try {
      return await task;
    } finally {
      pendingChildrenRef.current.delete(targetValue);
    }
  };

  // Город под полем PLZ. Индекс из пяти цифр спрашивается у сервера с
  // задержкой, чтобы набор «10115» не стоил четырёх запросов; ответ сервер
  // держит у себя, так что повторный тот же индекс отвечается сразу.
  const [plzPlace, setPlzPlace] = useState({ code: "", city: "", state: "" });
  useEffect(() => {
    const code = String(newAd.postalCode || "").trim();
    if (!/^\d{5}$/.test(code)) {
      setPlzPlace({ code: "", city: "", state: "" });
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await apiFetchJson(`/api/plz/${code}`);
        if (cancelled) return;
        setPlzPlace({ code, city: String(data?.city || ""), state: String(data?.state || "") });
      } catch (_) {
        // Справочник недоступен — подписи просто не будет.
        if (!cancelled) setPlzPlace({ code, city: "", state: "" });
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [newAd.postalCode]);

  // Населённые пункты индекса: у одного PLZ их бывает несколько (28217 —
  // четыре района Walle), и на Kleinanzeigen при подаче выбирают один из них.
  // Список — как в форме KL, от имени аккаунта; выбранный уходит при
  // публикации (locationId). Пункт один — выбирать нечего, поле не показано.
  const [plzLocations, setPlzLocations] = useState({ code: "", items: [], loaded: false });
  useEffect(() => {
    const code = String(newAd.postalCode || "").trim();
    const accountId = String(newAd.accountId || "").trim();
    if (!isOpen || !accountId || !/^\d{5}$/.test(code)) {
      setPlzLocations({ code: "", items: [], loaded: false });
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await apiFetchJson(`/api/accounts/${accountId}/plz-locations?zip=${code}`, { timeoutMs: 45000 });
        if (cancelled) return;
        const items = (Array.isArray(data?.locations) ? data.locations : [])
          .map((item) => ({ id: String(item?.id || ""), name: String(item?.name || "") }))
          .filter((item) => item.id && item.name);
        setPlzLocations({ code, items, loaded: true });
      } catch (_) {
        // Kleinanzeigen не ответил — поля не будет, публикатор выберет сам.
        if (!cancelled) setPlzLocations({ code, items: [], loaded: false });
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [isOpen, newAd.accountId, newAd.postalCode]);
  // Выбор по умолчанию — первый пункт, как в форме KL; сохранённый в черновике
  // остаётся, если он есть в списке. Список пришёл и выбирать нечего — выбор
  // снимается.
  useEffect(() => {
    const code = String(newAd.postalCode || "").trim();
    if (!plzLocations.loaded || plzLocations.code !== code) return;
    const { items } = plzLocations;
    setNewAd((prev) => {
      if (String(prev?.postalCode || "").trim() !== code) return prev;
      if (items.length < 2) {
        return prev.locationId || prev.locationName ? { ...prev, locationId: "", locationName: "" } : prev;
      }
      const current = items.find((item) => item.id === String(prev.locationId || ""));
      if (current) return current.name === prev.locationName ? prev : { ...prev, locationName: current.name };
      return { ...prev, locationId: items[0].id, locationName: items[0].name };
    });
  }, [plzLocations, newAd.postalCode, setNewAd]);
  const fetchAccountPostalCode = async () => {
    if (!newAd?.accountId) {
      alert(t("Сначала выберите аккаунт"));
      return;
    }
    setLoadingPlz(true);
    try {
      // Обычно ответ за секунды (форма публикации одним запросом). Запас — на
      // редкий случай, когда сервер дочитывает PLZ браузером.
      const data = await apiFetchJson(`/api/accounts/${newAd.accountId}/plz`, { timeoutMs: 120000 });
      if (data?.success && data?.postalCode) {
        setNewAd((prev) => withPostalCode(prev, data.postalCode));
        // Kleinanzeigen не ответил, и подставлен сохранённый PLZ — об этом надо
        // сказать, иначе он выглядит свежим.
        if (data?.fromCache && data?.warning) {
          alert(t(data.warning));
        }
      } else {
        alert(data?.error || t("Не удалось получить PLZ аккаунта"));
      }
    } catch (error) {
      alert(error?.message || t("Не удалось получить PLZ аккаунта"));
    } finally {
      setLoadingPlz(false);
    }
  };

  const fetchProxyPostalCode = async () => {
    if (!newAd?.accountId) {
      alert(t("Сначала выберите аккаунт"));
      return;
    }
    setLoadingProxyPlz(true);
    try {
      const data = await apiFetchJson(`/api/accounts/${newAd.accountId}/proxy-plz?force=1`);
      if (data?.success && data?.postalCode) {
        setNewAd((prev) => withPostalCode(prev, data.postalCode));
        if (data?.warning) {
          alert(data.warning);
        }
      } else {
        alert(data?.error || t("Не удалось получить PLZ прокси"));
      }
    } catch (error) {
      alert(error?.message || t("Не удалось получить PLZ прокси"));
    } finally {
      setLoadingProxyPlz(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const accountId = String(newAd?.accountId || "").trim();
    if (!accountId) {
      lastAutofillAccountIdRef.current = "";
      return;
    }
    if (lastAutofillAccountIdRef.current === accountId) return;
    lastAutofillAccountIdRef.current = accountId;

    const selected = accounts.find((account) => String(account?.id || "") === accountId);
    if (!selected) return;
    const accountPostalCode = String(selected?.postalCode || "").trim();
    const proxyPostalCode = String(selected?.proxyPostalCode || "").trim();
    const nextPostalCode = accountPostalCode || proxyPostalCode || "";
    setNewAd((prev) => {
      if (String(prev?.accountId || "").trim() !== accountId) return prev;
      return withPostalCode(prev, nextPostalCode);
    });
  }, [isOpen, accounts, newAd?.accountId, setNewAd]);

  useEffect(() => {
    if (!isOpen) return;
    if (categoryPath.length !== 1) return;
    const rootValue = String(categoryPath[0] || "");
    if (!rootValue) return;

    if (prefetchRootRef.current !== rootValue) {
      prefetchRootRef.current = rootValue;
      prefetchedTargetsRef.current = new Set();
      prefetchInFlightRef.current = false;
    }

    if (prefetchInFlightRef.current) return;
    const current = getCurrentCategories();
    if (!current.length) return;

    const token = ++prefetchTokenRef.current;
    prefetchInFlightRef.current = true;

    (async () => {
      for (const node of current) {
        if (prefetchTokenRef.current !== token) return;
        const key = getCategoryValue(node);
        if (!key) continue;
        if (prefetchedTargetsRef.current.has(key)) continue;
        prefetchedTargetsRef.current.add(key);
        if (node.children?.length || node.childrenLoaded) continue;
        await requestChildren(node);
      }
    })().finally(() => {
      if (prefetchTokenRef.current === token) {
        prefetchInFlightRef.current = false;
      }
    });
  }, [isOpen, newAd.accountId, categoryPath, localCategories]);

  const getCategoryBreadcrumb = () => {
    const breadcrumb = [];
    let current = localCategories;

    for (const pathItem of categoryPath) {
      const found = current.find((cat) =>
        (cat.id && String(cat.id) === String(pathItem)) ||
        (cat.url && String(cat.url) === String(pathItem))
      );
      if (found) {
        breadcrumb.push(found);
        current = filterCategoryChildren(found.children || [], found);
      }
    }

    return breadcrumb;
  };


  const breadcrumb = getCategoryBreadcrumb();
  const normalizedDraftImageUrls = useMemo(
    () => (
      Array.isArray(draftImageUrls)
        ? draftImageUrls.map((item) => String(item || "").trim()).filter(Boolean)
        : []
    ),
    [draftImageUrls]
  );
  // Сетка — в общем порядке (снимки черновика и свои вперемешку). Свой файл
  // показывается по превью; пока превью не готово (кадр после добавления),
  // плитки нет — она появится в следующей отрисовке.
  const combinedImageItems = useMemo(() => {
    const previewByFile = new Map(imagePreviews.map((preview) => [preview.file, preview.url]));
    const files = Array.isArray(adImages) ? adImages : [];
    return buildPhotoSequence(photoOrder, normalizedDraftImageUrls, files)
      .map((token) => {
        if (token.kind === "draft") {
          const draftIndex = normalizedDraftImageUrls.indexOf(token.url);
          return { key: `draft-${token.url}`, url: token.url, source: "draft", draftIndex };
        }
        const localIndex = files.indexOf(token.file);
        const url = previewByFile.get(token.file);
        if (!url) return null;
        return { key: `local-${url}`, url, source: "local", localIndex };
      })
      .filter(Boolean);
  }, [photoOrder, normalizedDraftImageUrls, imagePreviews, adImages]);
  const quickCategoryOptions = useMemo(
    () => buildQuickCategoryOptions(localCategories, []),
    [localCategories, categoryLangStamp]
  );
  const quickCategoryQueryNormalized = String(categoryQuickQuery || "").trim().toLowerCase();
  // Кнопки нейросети — одной формы с «Вставить» у подсказки ответа в диалоге:
  // скруглённый прямоугольник, акцентная подложка, без рамки.
  const aiChipStyle = (enabled) => ({
    borderRadius: "9px",
    border: "none",
    background: enabled ? aa(500, 0.16) : aa(500, 0.08),
    color: enabled ? A[200] : "#707684",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: "inherit",
    cursor: enabled ? "pointer" : "default",
    padding: "6px 10px",
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    transition: "background 0.15s ease, color 0.15s ease"
  });
  const aiChipHover = (enabled) => ({
    onMouseEnter: (event) => { if (enabled) { event.currentTarget.style.background = aa(500, 0.26); event.currentTarget.style.color = A[100]; } },
    onMouseLeave: (event) => { event.currentTarget.style.background = enabled ? aa(500, 0.16) : aa(500, 0.08); event.currentTarget.style.color = enabled ? A[200] : "#707684"; }
  });
  const REWRITE_LIMITS = { title: 65, description: 4000 };
  const rewriteReady = (field) => String(newAd?.[field] || "").trim().length >= (field === "title" ? 3 : 10);
  const setRewriteState = (field, patch) => setRewriteByField((prev) => ({ ...prev, [field]: { ...prev[field], ...patch } }));

  // Одно поле за нажатие. Второе поле уходит модели как контекст: название
  // переводится с оглядкой на описание и наоборот, но не переписывается.
  const requestRewrite = async (field) => {
    if (rewriteByField[field]?.status === "loading" || !rewriteReady(field)) return;
    const title = String(newAd?.title || "").trim();
    const description = String(newAd?.description || "").trim();
    setRewriteState(field, { status: "loading", error: "", backup: null });
    try {
      const data = await apiFetchJson("/api/ads/rewrite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ field, title, description }),
        timeoutMs: 90000
      });
      const next = String(data?.[field] || "").slice(0, REWRITE_LIMITS[field]);
      if (!next) throw new Error(t("Модель вернула пустой текст — повторите."));
      const truncatedFlag = field === "title" ? "_titleTruncated" : "_descTruncated";
      setNewAd((prev) => ({ ...prev, [field]: next, [truncatedFlag]: false }));
      setRewriteState(field, { status: "done", error: "", backup: newAd?.[field] || "", source: "rewrite" });
    } catch (error) {
      setRewriteState(field, { status: "error", error: error?.message || t("Не удалось переписать текст"), backup: null });
    }
  };

  const undoRewrite = (field) => {
    const backup = rewriteByField[field]?.backup;
    if (backup === null || backup === undefined) return;
    const truncatedFlag = field === "title" ? "_titleTruncated" : "_descTruncated";
    setNewAd((prev) => ({ ...prev, [field]: backup, [truncatedFlag]: false }));
    setRewriteState(field, { status: "idle", error: "", backup: null });
  };

  // Кнопка той же формы, что «Подобрать» у категории, и строка состояния рядом.
  const renderRewriteControl = (field) => {
    const state = rewriteByField[field] || {};
    const ready = rewriteReady(field);
    const busy = state.status === "loading";
    const doneLabel = state.source === "photos"
      ? (field === "title" ? t("Составлено по фото: название") : t("Составлено по фото: описание"))
      : (field === "title" ? t("Переписано на немецком: название") : t("Переписано на немецком: описание"));
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", minHeight: "26px" }}>
        <button
          type="button"
          onClick={() => requestRewrite(field)}
          disabled={!ready || busy}
          title={t("Перевести на немецкий и переписать другими словами")}
          style={aiChipStyle(ready && !busy)}
          {...aiChipHover(ready && !busy)}
        >
          {busy ? <LoadingIcon size={12} /> : <SparklesIcon size={12} />}
          {busy ? t("Переписываем...") : t("Уникализировать")}
        </button>
        {state.status === "done" && (
          <span style={{ fontSize: "11.5px", color: "#86efac", display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            {doneLabel}
            <button
              type="button"
              onClick={() => undoRewrite(field)}
              style={{
                border: 0, background: "transparent", padding: 0, fontFamily: "inherit",
                fontSize: "11.5px", color: A[300], cursor: "pointer", textDecoration: "underline dotted"
              }}
            >
              {t("Вернуть как было")}
            </button>
          </span>
        )}
        {state.status === "error" && (
          <span style={{ fontSize: "11.5px", color: "#fda4af" }}>{state.error}</span>
        )}
      </div>
    );
  };

  // Снимок ужимается до 1024px по длинной стороне и уходит как JPEG: модели
  // хватает, а тело запроса остаётся небольшим даже при восьми фото.
  const shrinkImageForModel = (source) => new Promise((resolve) => {
    const image = new Image();
    const url = source instanceof Blob ? URL.createObjectURL(source) : String(source || "");
    const cleanup = () => { if (source instanceof Blob) URL.revokeObjectURL(url); };
    image.onload = () => {
      try {
        const longest = Math.max(image.naturalWidth, image.naturalHeight) || 1;
        const scale = Math.min(1, 1024 / longest);
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
        cleanup();
        resolve({ mimeType: "image/jpeg", data: dataUrl.replace(/^data:[^;]+;base64,/, "") });
      } catch (_) { cleanup(); resolve(null); }
    };
    image.onerror = () => { cleanup(); resolve(null); };
    if (!(source instanceof Blob)) image.crossOrigin = "anonymous";
    image.src = url;
  });

  const requestPhotoDescribe = async () => {
    if (photoDescribe.status === "loading" || !combinedImageItems.length) return;
    setPhotoDescribe({ status: "loading", error: "", summary: "", notes: {} });
    try {
      const items = combinedImageItems.slice(0, 8);
      const shrunk = await Promise.all(items.map(async (item) => {
        if (item.source === "local") {
          const preview = imagePreviews[item.localIndex];
          return preview?.file ? shrinkImageForModel(preview.file) : null;
        }
        try {
          const response = await fetch(item.url);
          const blob = await response.blob();
          return shrinkImageForModel(blob);
        } catch (_) { return null; }
      }));
      const sent = [];
      const sentItems = [];
      shrunk.forEach((image, index) => {
        if (!image) return;
        sent.push(image);
        sentItems.push(items[index]);
      });
      if (!sent.length) throw new Error(t("Не удалось прочитать фото"));

      const data = await apiFetchJson("/api/ads/describe-photos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          images: sent,
          title: String(newAd?.title || "").trim(),
          description: String(newAd?.description || "").trim()
        }),
        timeoutMs: 120000
      });

      const done = [];
      const nextTitle = String(data?.title || "").slice(0, 65);
      const nextDescription = String(data?.description || "").slice(0, 4000);
      if (nextTitle) {
        setRewriteState("title", { status: "done", error: "", backup: newAd?.title || "", source: "photos" });
        done.push(t("название объявления"));
      }
      if (nextDescription) {
        setRewriteState("description", { status: "done", error: "", backup: newAd?.description || "", source: "photos" });
        done.push(t("текст описания"));
      }
      setNewAd((prev) => ({
        ...prev,
        ...(nextTitle ? { title: nextTitle, _titleTruncated: false } : {}),
        ...(nextDescription ? { description: nextDescription, _descTruncated: false } : {})
      }));
      if (data?.category?.id) {
        applyCategorySuggestion(data.category);
        done.push(t("категория"));
      }

      // Обложка: выбранный снимок становится первым — любой, и свой файл, и
      // снимок черновика (порядок теперь общий).
      const coverItem = sentItems[Number(data?.coverIndex) || 0];
      let coverNote = "";
      const coverPosition = coverItem ? combinedImageItems.indexOf(coverItem) : -1;
      if (coverPosition > 0) {
        movePhoto(coverPosition, 0);
        coverNote = tf`обложка — фото ${coverPosition + 1}`;
      }

      const notes = {};
      (Array.isArray(data?.photos) ? data.photos : []).forEach((entry) => {
        const item = sentItems[Number(entry?.index)];
        if (!item || !Array.isArray(entry?.labels) || !entry.labels.length) return;
        notes[item.key] = entry.labels;
      });
      const warnings = Object.keys(notes).length;
      setPhotoDescribe({
        status: "done",
        error: "",
        summary: [
          done.length ? tf`Готово: ${done.join(", ")}` : t("Модель ничего не предложила"),
          coverNote,
          warnings ? tf`замечаний к фото: ${warnings}` : ""
        ].filter(Boolean).join(" · "),
        notes
      });
    } catch (error) {
      setPhotoDescribe({ status: "error", error: error?.message || t("Не удалось описать фото"), summary: "", notes: {} });
    }
  };

  const requestCategorySuggestions = async () => {
    const title = String(newAd?.title || "").trim();
    if (title.length < 4) return;
    setCategorySuggest({ status: "loading", items: [], error: "", forTitle: title });
    try {
      const data = await apiFetchJson("/api/categories/suggest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          description: String(newAd?.description || "").slice(0, 600)
        }),
        timeoutMs: 40000
      });
      const items = Array.isArray(data?.suggestions) ? data.suggestions : [];
      setCategorySuggest({ status: items.length ? "done" : "error", items, error: items.length ? "" : t("Подходящая категория не нашлась"), forTitle: title });
    } catch (error) {
      // Настоящая причина показывается всегда. Раньше при автоматическом
      // запросе она глушилась, и обрыв связи выглядел так же, как «модель
      // ничего не подобрала», — то есть по полосе нельзя было понять, что
      // сломалось.
      setCategorySuggest({
        status: "error",
        items: [],
        error: error?.message || t("Не удалось подобрать категорию"),
        forTitle: title
      });
    }
  };

  // Подсказка запрашивается один раз на открытие списка и только под то
  // название, которое сейчас в форме: постоянный опрос на каждую букву стоил бы
  // обращения к модели на каждое нажатие клавиши.
  useEffect(() => {
    if (!categoryPickerOpen) return;
    const title = String(newAd?.title || "").trim();
    if (title.length < 4) return;
    if (categorySuggest.status === "loading") return;
    if (categorySuggest.forTitle === title && categorySuggest.status !== "idle") return;
    requestCategorySuggestions();
    // eslint-disable-next-line
  }, [categoryPickerOpen, newAd?.title]);

  useEffect(() => {
    if (categoryPickerOpen) setCategorySearch("");
  }, [categoryPickerOpen]);

  // Дерево у Kleinanzeigen в два уровня и целиком помещается на экран, поэтому
  // список плоский: раздел с подразделами сразу, без переходов по уровням. При
  // вводе он превращается в один список совпадений с полным путём у каждого.
  const categorySearchNormalized = String(categorySearch || "").trim().toLowerCase();
  const categorySearchMatches = useMemo(() => {
    if (categorySearchNormalized.length < 1) return [];
    const scored = [];
    for (const option of quickCategoryOptions) {
      const index = option.searchText.indexOf(categorySearchNormalized);
      if (index === -1) continue;
      const startsWith = option.label.toLowerCase().startsWith(categorySearchNormalized) ? 0 : 1;
      scored.push({ option, score: index + startsWith });
    }
    scored.sort((a, b) => a.score - b.score);
    return scored.slice(0, 60).map((item) => item.option);
  }, [quickCategoryOptions, categorySearchNormalized]);

  const applyCategorySuggestion = (suggestion) => {
    const pathValues = buildPathToNode(localCategories, suggestion?.id);
    if (!pathValues.length) return;
    const labels = pathValues.map((value) => {
      const node = findNode(localCategories, value);
      return node ? getCategoryLabel(node) : value;
    });
    handleQuickCategorySelect({
      value: String(suggestion.id),
      label: labels[labels.length - 1] || "",
      pathValues: pathValues.map((item) => String(item)),
      fullPathLabel: labels.join(" / ")
    });
    setCategoryPickerOpen(false);
  };

  const handleQuickCategorySelect = (option) => {
    if (!option || !Array.isArray(option.pathValues) || !option.pathValues.length) return;
    const nextPath = option.pathValues.map((item) => String(item));
    setDidRestorePath(true);
    setCategoryPath(nextPath);
    setCategoryChildrenError("");
    setCategoryQuickQuery(option.fullPathLabel || option.label || "");
    setCategoryQuickOpen(false);
    const { selectedNode, numericNode } = resolveCategorySelection(nextPath);
    setNewAd((prev) => {
      const next = {
        ...prev,
        categoryId: numericNode?.id ? String(numericNode.id) : "",
        categoryUrl: selectedNode?.url || numericNode?.url || "",
        categoryKey: nextPath.join(">"),
        categoryPath: [...nextPath]
      };
      if (!next.categoryId || next.categoryId !== prev.categoryId) {
        if (setExtraFieldValues) {
          setExtraFieldValues({});
        }
      }
      return next;
    });
  };
  // Категория (design 2a §7): collapsed to a single row — path above, leaf below.
  // The full tree stays exactly as it is and opens on click.
  const categoryLabels = breadcrumb.map((item) => getCategoryLabel(item)).filter(Boolean);
  const categoryLeafLabel = categoryLabels.length
    ? categoryLabels[categoryLabels.length - 1]
    : (newAd?.categoryId || newAd?.categoryUrl ? t("Категория выбрана") : t("Не выбрана"));
  const categoryTrailLabel = categoryLabels.length > 1
    ? categoryLabels.slice(0, -1).join(" › ")
    : t("Категория");

  // Полный путь рубрики для предпросмотра: «Elektronik › Audio & Hifi › …».
  const previewCategoryPath = (() => {
    const labels = breadcrumb.map((item) => getCategoryLabel(item)).filter(Boolean);
    if (labels.length) return labels.join(" › ");
    if (newAd?.categoryId || newAd?.categoryUrl) return t("Категория выбрана");
    return "";
  })();

  // Название — обычным полем с подписью и счётчиком, как описание. Прежде оно
  // стояло в шапке крупной строкой без рамки и не читалось как поле, а шапка с
  // ним занимала треть окна. На столе — первым полем левой колонки, на
  // телефоне — в шапке (она прокручивается вместе с формой).
  const renderTitleField = () => {
    const counterColor = titleLength >= 65 ? "#f87171" : (titleLength >= 60 ? "#fde68a" : "#707684");
    const handleTitleChange = (e) => {
      const val = e.target.value;
      if (val.length <= 65) {
        setNewAd((prev) => ({ ...prev, title: val, _titleTruncated: false }));
      } else {
        setNewAd((prev) => ({ ...prev, title: val.slice(0, 65), _titleTruncated: true }));
      }
    };
    const handleTitlePaste = (e) => {
      const paste = e.clipboardData?.getData("text") || "";
      const current = newAd.title || "";
      const sel = e.target.selectionStart ?? current.length;
      const selEnd = e.target.selectionEnd ?? sel;
      const result = current.slice(0, sel) + paste + current.slice(selEnd);
      if (result.length > 65) {
        e.preventDefault();
        setNewAd((prev) => ({ ...prev, title: result.slice(0, 65), _titleTruncated: true }));
      }
    };
      return (
        <div style={sectionCardStyle}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "10px", marginBottom: "8px" }}>
            <span style={sectionTitleStyle}>{t("Название")}</span>
            <span style={{ fontSize: "11px", color: counterColor, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
              {titleLength} / 65
            </span>
          </div>
          <input
            type="text"
            value={newAd.title}
            maxLength={65}
            onChange={handleTitleChange}
            onPaste={handleTitlePaste}
            placeholder={t("Название объявления")}
            style={{ ...inputStyle, fontSize: "15px", fontWeight: 600, letterSpacing: "-0.005em" }}
            onFocus={(e) => { e.currentTarget.style.borderColor = inputFocusColor; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = inputBlurColor; }}
          />
          <div style={{ height: "3px", marginTop: "8px", borderRadius: "2px", background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
            <div style={{
              width: `${Math.min(100, Math.round((titleLength / 65) * 100))}%`,
              height: "100%",
              background: titleProgressColor,
              borderRadius: "2px",
              transition: "width 0.15s ease, background 0.15s ease"
            }} />
          </div>
          <div style={{ marginTop: "8px", minHeight: "26px" }}>
            {renderRewriteControl("title")}
          </div>
        </div>
      );
  };

  // Фото: на столе — в левой колонке под описанием, на телефоне — первым блоком
  // правой (как было).
  const renderPhotosBlock = () => (
          <div>
            {/* Фото (design 2a): heading + count caption, 4-across 1:1 tiles, the
                whole block is the drop zone, last tile is the dashed "+" button. */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", marginBottom: "8px", flexWrap: "wrap" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <span style={sectionTitleStyle}>{t("Фото")}</span>
                {/* Объявление по фото: та же форма, что у «Уникализировать». */}
                {aiPrefs.aiPhotoDescribe && (
                <button
                  type="button"
                  onClick={requestPhotoDescribe}
                  disabled={!combinedImageItems.length || photoDescribe.status === "loading"}
                  title={t("Модель посмотрит фото и напишет название, описание, подберёт категорию и обложку")}
                  style={aiChipStyle(combinedImageItems.length > 0 && photoDescribe.status !== "loading")}
                  {...aiChipHover(combinedImageItems.length > 0 && photoDescribe.status !== "loading")}
                >
                  {photoDescribe.status === "loading" ? <LoadingIcon size={12} /> : <SparklesIcon size={12} />}
                  {photoDescribe.status === "loading" ? t("Смотрим фото...") : t("Описать по фото")}
                </button>
                )}
              </span>
              <span style={{ fontSize: "11px", color: "#707684", whiteSpace: "nowrap" }}>
                {combinedImageItems.length} {t("из 20")}{combinedImageItems.length > 1 ? t(" · порядок — перетаскиванием или кнопками на фото") : ""}
              </span>
            </div>
            {(photoDescribe.status === "done" || photoDescribe.status === "error") && (
              <div style={{ fontSize: "11.5px", color: photoDescribe.status === "error" ? "#fda4af" : "#86efac", marginBottom: "8px" }}>
                {photoDescribe.status === "error" ? photoDescribe.error : photoDescribe.summary}
              </div>
            )}
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageInputChange}
              style={{ display: "none" }}
            />
            {(
              <div
                onDragOver={(event) => { event.preventDefault(); }}
                onDrop={(event) => { handleImageDrop(event); }}
                style={{
                  display: "grid",
                  // На столе фото — в широкой колонке рядом с описанием, плитки
                  // крупнее; на телефоне — по четыре в ряд, как было.
                  gridTemplateColumns: isPhoneLayout ? "repeat(4, 1fr)" : "repeat(auto-fill, minmax(112px, 1fr))",
                  gap: isPhoneLayout ? "8px" : "10px"
                }}
              >
                {combinedImageItems.map((item, index) => {
                  const dragging = photoDragIndex === index;
                  const notes = Array.isArray(photoDescribe.notes[item.key]) ? photoDescribe.notes[item.key] : [];
                  return (
                  <div
                    key={item.key}
                    data-photo-index={index}
                    className={`edit-photo-tile${isPhoneLayout ? " is-compact" : ""}${dragging ? " is-dragging" : ""}`}
                    onPointerDown={(event) => startPhotoDrag(event, index)}
                    style={{
                      position: "relative",
                      borderRadius: "12px",
                      overflow: "hidden",
                      background: "#0b1220",
                      border: dragging
                        ? `1px solid ${aa(400, 0.85)}`
                        : (index === 0 ? `1px solid ${aa(400, 0.45)}` : "1px solid rgba(141,147,163,0.2)"),
                      boxShadow: dragging
                        ? `0 0 0 3px ${aa(500, 0.28)}, 0 12px 28px rgba(0,0,0,0.45)`
                        : "none",
                      transform: dragging ? "scale(1.04)" : "none",
                      zIndex: dragging ? 2 : "auto",
                      aspectRatio: "1 / 1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: photoDragActive ? "grabbing" : "grab",
                      userSelect: "none",
                      transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease"
                    }}
                  >
                    <img
                      src={item.url}
                      alt={`preview-${index + 1}`}
                      draggable={false}
                      style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
                    />
                    {/* Метки — в левом верхнем углу столбиком: номер (или
                        «обложка») и замечание модели к фото. Низ плитки занят
                        кнопками порядка. */}
                    <div style={{
                      position: "absolute",
                      left: "6px",
                      top: "6px",
                      right: "34px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: "4px",
                      pointerEvents: "none"
                    }}>
                      {/* Плитки здесь мельче, чем в окне правки, и слово
                          «Обложка» не помещалось рядом с крестиком — обложка
                          помечена звездой, как и кнопка «Сделать обложкой». */}
                      <div
                        title={index === 0 ? t("Обложка") : undefined}
                        style={{
                          minWidth: "20px",
                          height: "20px",
                          padding: "0 6px",
                          boxSizing: "border-box",
                          borderRadius: "999px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "10.5px",
                          fontWeight: 700,
                          color: index === 0 ? onAccent(A[100]) : "#e2e4e9",
                          background: index === 0 ? aa(700, 0.78) : "rgba(8,9,12,0.7)",
                          border: index === 0 ? `1px solid ${aa(400, 0.5)}` : "1px solid rgba(141,147,163,0.25)",
                          pointerEvents: "auto"
                        }}
                      >
                        {index === 0 ? <StarIcon size={11} fill="currentColor" /> : index + 1}
                      </div>
                      {notes.length > 0 && (
                        <div
                          title={notes.join(", ")}
                          style={{
                            maxWidth: "100%",
                            padding: "2px 7px", borderRadius: "999px",
                            fontSize: "9.5px", fontWeight: 700, lineHeight: 1.3,
                            color: "#fde68a", background: "rgba(120,53,15,0.85)", border: "1px solid rgba(251,191,36,0.45)",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pointerEvents: "auto"
                          }}
                        >
                          {notes[0]}
                        </div>
                      )}
                    </div>
                    {combinedImageItems.length > 1 && (
                      <div className="edit-photo-controls">
                        <button
                          type="button"
                          className="edit-photo-ctl"
                          disabled={index === 0}
                          onClick={(event) => { event.stopPropagation(); movePhoto(index, index - 1); }}
                          title={t("Сдвинуть влево")}
                          aria-label={t("Сдвинуть влево")}
                        >
                          <ChevronLeftIcon size={14} />
                        </button>
                        {index > 0 && (
                          <button
                            type="button"
                            className="edit-photo-ctl"
                            onClick={(event) => { event.stopPropagation(); movePhoto(index, 0); }}
                            title={t("Сделать обложкой")}
                            aria-label={t("Сделать обложкой")}
                          >
                            <StarIcon size={12} />
                          </button>
                        )}
                        <button
                          type="button"
                          className="edit-photo-ctl"
                          disabled={index === combinedImageItems.length - 1}
                          onClick={(event) => { event.stopPropagation(); movePhoto(index, index + 1); }}
                          title={t("Сдвинуть вправо")}
                          aria-label={t("Сдвинуть вправо")}
                        >
                          <ChevronRightIcon size={14} />
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (item.source === "local") {
                          removeAdImage(item.localIndex);
                        } else {
                          removeDraftImage(item.draftIndex);
                        }
                      }}
                      style={{
                        position: "absolute",
                        top: "6px",
                        right: "6px",
                        width: "24px",
                        height: "24px",
                        borderRadius: "999px",
                        border: "1px solid rgba(239,68,68,0.4)",
                        background: "rgba(127,29,29,0.85)",
                        color: "#fecaca",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease"
                      }}
                      onMouseEnter={(event) => {
                        applyHoverStyles(event, {
                          background: "rgba(220,38,38,0.95)",
                          borderColor: "rgba(248,113,113,0.9)",
                          transform: "scale(1.12)"
                        });
                      }}
                      onMouseLeave={(event) => {
                        applyHoverStyles(event, {
                          background: "rgba(127,29,29,0.85)",
                          borderColor: "rgba(239,68,68,0.4)",
                          transform: "scale(1)"
                        });
                      }}
                      title={t("Удалить фото")}
                    >
                      <XIcon size={12} />
                    </button>
                    <button
                      type="button"
                      className="edit-photo-edit-btn"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        stopPhotoDrag();
                        setPhotoEditorItem(item);
                      }}
                      title={t("Редактировать фото")}
                      aria-label={t("Редактировать фото")}
                    >
                      <CropIcon size={12} />
                    </button>
                  </div>
                  );
                })}
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  title={t("Добавить фото")}
                  style={{
                    aspectRatio: "1 / 1",
                    borderRadius: "12px",
                    border: "1px dashed rgba(141,147,163,0.4)",
                    background: "rgba(8,9,12,0.5)",
                    color: "#8d93a3",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "border-color 0.15s ease, background 0.15s ease, color 0.15s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = aa(300, 0.55);
                    e.currentTarget.style.background = aa(800, 0.2);
                    e.currentTarget.style.color = A[200];
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(141,147,163,0.4)";
                    e.currentTarget.style.background = "rgba(8,9,12,0.5)";
                    e.currentTarget.style.color = "#8d93a3";
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
  );

  return (
    <div ref={overlayRef} className="modal-overlay ad-modal-root" {...overlayMotionProps(dialogState)} style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `radial-gradient(circle at 15% 10%, ${aa(800, 0.24)}, rgba(8,9,12,0.92) 46%, rgba(8,9,12,0.98) 100%)`,
      backdropFilter: "blur(10px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      // Above the mobile bottom tab bar (z 9990) so it can't cover the modal's
      // footer buttons; extra bottom padding keeps them off the safe-area edge.
      zIndex: 12000,
      // Сверху — с запасом под вырез: в веб-приложении на iPhone страница идёт
      // под строку состояния, и крестик оказывался под часами, где его не нажать.
      padding: isPhoneLayout
        ? "calc(14px + env(safe-area-inset-top, 0px)) 12px calc(14px + env(safe-area-inset-bottom, 0px))"
        : "20px"
    }}>
      <div className="modal-card modal-card-wide modal-card-tall ad-modal-card" data-modal-scroll="1" {...dialogMotionProps(dialogState)} style={{
        background: S.surface,
        borderRadius: "20px",
        // Header / body / footer carry their own padding (design 2a).
        padding: isPhoneLayout ? "0 12px" : 0,
        // Выше и чуть шире прежнего (было 1120×660): поля не теснятся, правая
        // колонка с настройками обычно помещается без прокрутки.
        width: isPhoneLayout ? "720px" : "1180px",
        maxWidth: "100%",
        height: isPhoneLayout ? "auto" : "860px",
        // На телефоне — по высоте окна за вычетом отступов, а не 90vh: в
        // веб-приложении на iPhone vh бывает больше видимой области (после
        // клавиатуры iOS укорачивает страницу), и карточка вылезала под часы.
        maxHeight: isPhoneLayout ? "100%" : "92vh",
        // На телефоне прокручивается вся карточка вместе с шапкой — граббер и
        // название уезжают вверх, как остальное; внизу липнет только подвал с
        // кнопками. На столе шапка и подвал стоят, прокручивается тело.
        overflowY: isPhoneLayout ? "auto" : "hidden",
        overflowX: "hidden",
        overscrollBehavior: "contain",
        WebkitOverflowScrolling: "touch",
        display: "flex",
        flexDirection: "column",
        border: "1px solid rgba(141,147,163,0.2)",
        boxShadow: "0 34px 72px rgba(0,0,0,0.54)",
        color: "#e2e4e9"
      }}>
        {/* Decorative gradient stripe */}
        <div style={{
          height: "0px",
          margin: 0
        }} />
        {/* Header (design 2a): status badge → eBay grabber row → title input →
            character counter. The grabber lives here so importing is always the
            first action; the title doubles as the modal heading (17px). */}
        <div style={{
          flexShrink: 0,
          display: "flex",
          flexDirection: isPhoneLayout ? "column" : "row",
          alignItems: isPhoneLayout ? "stretch" : "center",
          justifyContent: "space-between",
          gap: isPhoneLayout ? "10px" : "16px",
          padding: isPhoneLayout ? "8px 0 12px" : "16px 24px",
          borderBottom: "1px solid rgba(141,147,163,0.14)"
        }}>
          {/* На столе шапка — одна строка: метка и граббер; название ушло в
              левую колонку полем с подписью. */}
          <div style={{
            display: "flex",
            flexDirection: isPhoneLayout ? "column" : "row",
            flexWrap: isPhoneLayout ? "nowrap" : "wrap",
            alignItems: isPhoneLayout ? "stretch" : "center",
            gap: isPhoneLayout ? "7px" : "8px 14px",
            minWidth: 0,
            flex: "1 1 auto"
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "9px" }}>
              <span style={{
                padding: "3px 9px",
                borderRadius: "999px",
                background: aa(500, 0.14),
                border: `1px solid ${aa(400, 0.3)}`,
                color: A[200],
                fontSize: "10.5px",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                whiteSpace: "nowrap"
              }}>
                {t("Новое объявление")}
              </span>
              {isPhoneLayout && (
                <button
                  type="button"
                  className="kl-plain-button"
                  onClick={onClose}
                  aria-label={t("Закрыть")}
                  style={{
                    width: "44px", height: "44px", flexShrink: 0, borderRadius: "12px",
                    background: `${sa(0.68)}`, border: "1px solid rgba(141,147,163,0.3)",
                    color: "#c6c9d1", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer"
                  }}
                >
                  <XIcon size={16} />
                </button>
              )}
            </div>

            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "9px",
              flex: isPhoneLayout ? undefined : "1 1 420px",
              flexWrap: isPhoneLayout ? "wrap" : "nowrap",
              minWidth: 0,
              padding: isPhoneLayout ? "8px" : "7px 8px 7px 10px",
              borderRadius: "13px",
              background: "rgba(8,9,12,0.55)",
              border: `1px solid ${aa(400, 0.22)}`
            }}>
              {/* Площадка выбирается здесь же, на месте прежней надписи «eBay».
                  Родной <select> взят намеренно: он открывается поверх модального
                  окна без забот о слоях и работает на телефоне как привычный
                  выбор системы. */}
              {/* На телефоне ссылка — первой строкой во всю ширину (её было не
                  разглядеть в узкой щели между площадкой и кнопкой), площадка и
                  «Забрать» — строкой ниже. */}
              <span style={{ position: "relative", flex: isPhoneLayout ? "1 1 auto" : "0 0 auto", display: "inline-flex" }}>
                <select
                  value={grabSiteId}
                  disabled={ebayImporting}
                  onChange={(event) => {
                    setGrabSiteId(event.target.value);
                    if (ebayImportError) setEbayImportError("");
                  }}
                  title={t("Площадка, с которой забирать объявление")}
                  style={{
                    appearance: "none",
                    WebkitAppearance: "none",
                    width: isPhoneLayout ? "100%" : undefined,
                    height: isPhoneLayout ? "40px" : undefined,
                    padding: isPhoneLayout ? "0 26px 0 12px" : "4px 22px 4px 9px",
                    borderRadius: "8px",
                    background: aa(500, 0.14),
                    border: `1px solid ${aa(400, 0.3)}`,
                    color: A[200],
                    fontSize: "11px",
                    fontWeight: 700,
                    fontFamily: "inherit",
                    cursor: ebayImporting ? "not-allowed" : "pointer",
                    outline: "none"
                  }}
                >
                  {GRAB_SITES.map((site) => (
                    <option key={site.id} value={site.id} style={{ background: S.elevated, color: "#e2e4e9" }}>
                      {site.label}
                    </option>
                  ))}
                </select>
                <span style={{
                  position: "absolute", right: "7px", top: "50%", transform: "translateY(-50%)",
                  pointerEvents: "none", color: A[300], display: "inline-flex"
                }}>
                  <ChevronRightIcon size={11} style={{ transform: "rotate(90deg)" }} />
                </span>
              </span>
              <input
                type="url"
                value={ebayUrl}
                disabled={ebayImporting}
                onChange={(e) => {
                  setEbayUrl(e.target.value);
                  if (ebayImportError) setEbayImportError("");
                }}
                placeholder={isPhoneLayout
                  ? t("Ссылка на лот")
                  : tf`Ссылка на лот (${(GRAB_SITES.find((site) => site.id === grabSiteId) || GRAB_SITES[0]).hint}) — заберём title, описание и фото`}
                style={{
                  flex: isPhoneLayout ? "1 1 100%" : "1 1 auto",
                  order: isPhoneLayout ? -1 : undefined,
                  minWidth: 0,
                  height: isPhoneLayout ? "40px" : "34px",
                  borderRadius: "10px",
                  border: "1px solid rgba(141,147,163,0.24)",
                  background: "rgba(0,0,0,0.34)",
                  color: "#e2e4e9",
                  fontFamily: "inherit",
                  fontSize: isPhoneLayout ? "16px" : "12.5px",
                  padding: "0 11px",
                  outline: "none",
                  transition: "border-color 0.15s ease"
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = aa(300, 0.58); }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(141,147,163,0.24)"; }}
              />
              <button
                type="button"
                className="kl-plain-button ad-accent-btn"
                onClick={handleEbayGrab}
                disabled={ebayImporting}
                style={{
                  height: isPhoneLayout ? "40px" : "34px",
                  padding: "0 14px",
                  borderRadius: "10px",
                  border: `1px solid ${aa(300, 0.38)}`,
                  background: `linear-gradient(135deg, ${aa(600, 0.92)}, ${aa(600, 0.85)})`,
                  color: onAccent(A[50]),
                  fontFamily: "inherit",
                  fontSize: "12px",
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  flexShrink: 0,
                  cursor: ebayImporting ? "not-allowed" : "pointer",
                  opacity: ebayImporting ? 0.75 : 1,
                  transition: "filter 0.15s ease"
                }}
                onMouseEnter={(e) => { if (!ebayImporting) e.currentTarget.style.filter = "brightness(1.08)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = "none"; }}
              >
                {ebayImporting ? (<><LoadingIcon size={13} /> {t("Импорт...")}</>) : t("Забрать")}
              </button>
            </div>

            {(ebayImportStatus || ebayImportError) && (
              <div style={{ fontSize: "11.5px", color: ebayImportError ? "#fda4af" : "#86efac", flexBasis: "100%" }}>
                {ebayImportError || ebayImportStatus}
              </div>
            )}

            {/* На телефоне название — в шапке, как прежде: оно уезжает вверх вместе
                с ней. На столе — первым полем левой колонки (renderTitleField). */}
            {isPhoneLayout && <div style={{ marginTop: "8px" }}>{renderTitleField()}</div>}
          </div>

          <button
            type="button"
            className="kl-plain-button"
            onClick={onClose}
            style={{
              display: isPhoneLayout ? "none" : "flex",
              width: "36px",
              height: "36px",
              flexShrink: 0,
              borderRadius: "12px",
              background: `${sa(0.68)}`,
              border: "1px solid rgba(141,147,163,0.3)",
              cursor: "pointer",
              color: "#c6c9d1",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.15s ease"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(127, 29, 29, 0.46)";
              e.currentTarget.style.borderColor = "rgba(248, 113, 113, 0.42)";
              e.currentTarget.style.color = "#fecaca";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = `${sa(0.68)}`;
              e.currentTarget.style.borderColor = "rgba(141,147,163,0.3)";
              e.currentTarget.style.color = "#c6c9d1";
            }}
          >
            <XIcon size={16} />
          </button>
        </div>

        <div style={{ flex: isPhoneLayout ? "0 0 auto" : "1 1 auto", minHeight: isPhoneLayout ? "auto" : 0, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "0", minWidth: 0, flex: isPhoneLayout ? "0 0 auto" : "1 1 auto", minHeight: isPhoneLayout ? "auto" : 0 }}>
          {/* Body (design 2a): content on the left, a 384px parameters column on
              the right (#0a0b0f, separated by a line). Photos sit at the top of
              that column, publishing parameters below it. Phones: one column. */}
          <div ref={bodyScrollRef} style={{
            overflowY: isPhoneLayout ? "visible" : "hidden",
            overflowX: "hidden",
            // Keep the scroll inside the modal: without this the page behind it
            // took over the gesture on touch devices.
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-y",
            flex: isPhoneLayout ? "0 0 auto" : "1 1 auto",
            minHeight: isPhoneLayout ? "auto" : 0,
            // Отступы сверху и снизу на телефоне: первая кнопка не липнет к
            // линии под шапкой, последняя — к линии над подвалом.
            padding: isPhoneLayout ? "16px 0 18px" : 0,
            display: "grid",
            gridTemplateColumns: isPhoneLayout ? "1fr" : "minmax(0, 1fr) 384px",
            alignItems: "stretch",
            columnGap: 0,
            rowGap: isPhoneLayout ? "20px" : 0
          }}>
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            minWidth: 0,
            gridColumn: isPhoneLayout ? "auto" : "1",
            gridRow: isPhoneLayout ? "auto" : "1",
            padding: isPhoneLayout ? 0 : "20px 24px 24px",
            overflowY: isPhoneLayout ? "visible" : "auto",
            minHeight: isPhoneLayout ? "auto" : 0,
            overscrollBehavior: "contain",
            order: isPhoneLayout ? 3 : undefined
          }}>

          {!isPhoneLayout && renderTitleField("card")}

          <div style={sectionCardStyle}>
            {/* Reference: heading on the left, character counter on the same
                baseline at the right. */}
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "10px", marginBottom: "8px" }}>
              <span style={sectionTitleStyle}>{t("Описание")}</span>
              <span style={{
                fontSize: "11px",
                color: (newAd.description || "").length >= 4000 ? "rgba(239,68,68,0.85)" : "#707684",
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap"
              }}>
                {(newAd.description || "").length} / 4000
              </span>
            </div>
            <textarea
              value={newAd.description}
              maxLength={4000}
              onChange={(e) => {
                const val = e.target.value;
                if (val.length <= 4000) {
                  setNewAd((prev) => ({ ...prev, description: val, _descTruncated: false }));
                } else {
                  setNewAd((prev) => ({ ...prev, description: val.slice(0, 4000), _descTruncated: true }));
                }
              }}
              onPaste={(e) => {
                const paste = e.clipboardData?.getData("text") || "";
                const current = newAd.description || "";
                const sel = e.target.selectionStart ?? current.length;
                const selEnd = e.target.selectionEnd ?? sel;
                const result = current.slice(0, sel) + paste + current.slice(selEnd);
                if (result.length > 4000) {
                  e.preventDefault();
                  setNewAd((prev) => ({ ...prev, description: result.slice(0, 4000), _descTruncated: true }));
                }
              }}
              placeholder={t("Введите описание (10-4000 символов)")}
              rows="5"
              style={{
                ...inputStyle,
                minHeight: isPhoneLayout ? "140px" : "190px",
                lineHeight: 1.55,
                resize: "vertical",
                fontFamily: "inherit",
                borderColor: (newAd.description || "").length >= 4000 ? "rgba(239,68,68,0.5)" : undefined
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = (newAd.description || "").length >= 4000 ? "rgba(239,68,68,0.7)" : inputFocusColor}
              onBlur={(e) => e.currentTarget.style.borderColor = (newAd.description || "").length >= 4000 ? "rgba(239,68,68,0.5)" : inputBlurColor}
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap", marginTop: "6px", minHeight: "26px" }}>
              {renderRewriteControl("description")}
              <span style={{
                fontSize: "11px",
                color: newAd._descTruncated ? "rgba(239,68,68,0.9)" : "transparent",
                transition: "color 0.2s"
              }}>
                {newAd._descTruncated ? t("Текст обрезан до 4000 символов") : ""}
              </span>
            </div>
          </div>

          {!isPhoneLayout && renderPhotosBlock()}

          {!loadingExtraFields && visibleExtraFields.length > 0 && (
            <div style={{ ...sectionCardStyle, marginTop: "4px", order: isPhoneLayout ? -1 : undefined }}>
              <label style={labelStyle}>{t("Параметры категории")}</label>
              {/* Параметры категории (design 2a §8/49): two columns; an unfilled
                  required parameter is flagged amber and marked so «Опубликовать»
                  can scroll to the first one. */}
              <div style={{ display: "grid", gridTemplateColumns: isPhoneLayout ? "1fr" : "1fr 1fr", gap: "10px" }}>
                {visibleExtraFields.map((field) => {
                  const key = field.name || field.label;
                  const value = key ? (extraFieldValues?.[key] || "") : "";
                  const isMissingRequired = Boolean(field.required) && !String(value ?? "").trim();
                  const fieldOptions = resolveExtraFieldOptions(field);
                  const fieldTypeToken = String(field.type || "").toUpperCase();
                  const fieldInputType = fieldTypeToken === "DATE"
                    ? "month"
                    : (["LONG", "INTEGER", "DECIMAL"].includes(fieldTypeToken) ? "number" : "text");
                  return (
                    <div
                      key={field.name || field.label}
                      data-missing-required={isMissingRequired ? "1" : undefined}
                      style={{ display: "flex", flexDirection: "column", gap: "6px", minWidth: 0 }}
                    >
                      {/* Reference: no boxed wrapper — the label itself turns amber
                          and the select keeps its normal shape. */}
                      <span style={{ fontSize: "12px", color: isMissingRequired ? "#fde68a" : "#8d93a3" }}>
                        {isMissingRequired ? (<><span style={{ fontWeight: 700 }}>{catText(field.label)}</span> {t("· обязательно")}</>) : catText(field.label)}
                      </span>
                      {fieldOptions.length > 0 ? (
                        <StyledSelect
                          value={value}
                          onChange={(nextValue) => setExtraFieldValues((prev) => {
                            const next = { ...prev, [key]: nextValue };
                            // Сменили родительский параметр — прежний выбор в
                            // зависимом от него поле больше не годится: у нового
                            // значения свой список, а то и нет поля вовсе.
                            (Array.isArray(extraFields) ? extraFields : []).forEach((other) => {
                              if (other?.dependsOn !== key) return;
                              const otherKey = other.name || other.label;
                              if (otherKey) delete next[otherKey];
                            });
                            return next;
                          })}
                          options={fieldOptions.map((option) => ({
                            value: option.value,
                            label: catText(option.label)
                          }))}
                          placeholder={t("Выберите")}
                          emptyText={t("Нет вариантов")}
                          ariaLabel={catText(field.label)}
                        />
                      ) : (
                        /* Числа и даты Kleinanzeigen не предлагает списком —
                           пробег, площадь, год выпуска вводятся руками. Раньше
                           для них рисовался тот же выпадающий список, но пустой:
                           обязательное поле, которое нечем заполнить, наглухо
                           перекрывало публикацию в Авто и Недвижимости.
                           Месяц даёт ровно тот формат, что у KL в объявлениях
                           (ГГГГ-ММ). */
                        <input
                          type={fieldInputType}
                          value={value}
                          inputMode={fieldInputType === "number" ? "numeric" : undefined}
                          step={String(field.type).toUpperCase() === "DECIMAL" ? "any" : undefined}
                          min={fieldInputType === "number" ? "0" : undefined}
                          placeholder={field.unit ? tf`в ${field.unit}` : t("Введите значение")}
                          aria-label={catText(field.label)}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            setExtraFieldValues((prev) => ({ ...prev, [key]: nextValue }));
                          }}
                          style={inputStyle}
                          onFocus={(event) => { event.currentTarget.style.borderColor = inputFocusColor; }}
                          onBlur={(event) => { event.currentTarget.style.borderColor = inputBlurColor; }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          </div>
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            minWidth: 0,
            gridColumn: isPhoneLayout ? "auto" : "2",
            gridRow: isPhoneLayout ? "auto" : "1",
            background: isPhoneLayout ? "transparent" : S.bg,
            borderLeft: isPhoneLayout ? "none" : "1px solid rgba(141,147,163,0.14)",
            padding: isPhoneLayout ? 0 : "20px",
            overflowY: isPhoneLayout ? "visible" : "auto",
            minHeight: isPhoneLayout ? "auto" : 0,
            overscrollBehavior: "contain",
            order: isPhoneLayout ? 1 : undefined
          }}>
          {isPhoneLayout && renderPhotosBlock()}

          {/* Mobile: Аккаунт / Категория / параметры / PLZ read as a single
              surface with divided rows (reference). On desktop the wrapper is
              display:contents, so the column layout is unchanged. */}
          <div
            className={isPhoneLayout ? "ad-mobile-group" : undefined}
            style={isPhoneLayout ? {
              display: "flex",
              flexDirection: "column",
              // Must not shrink: as a flex child with overflow:hidden it collapsed
              // to a sliver and the rows inside disappeared.
              flexShrink: 0,
              border: "none",
              background: "transparent"
            } : { display: "contents" }}
          >
          <div style={sectionCardStyle}>
            <label style={labelStyle}>{t("Аккаунт")}</label>
            {/* Тот же выпадающий список, что у остальных полей (.ui-select*),
                только строки богаче: аватар, прокси, оценка продавца. */}
            <div ref={accountDropdownRef} className={`ui-select${accountDropdownOpen ? " is-open" : ""}`}>
              <button
                type="button"
                className="ui-select-trigger ui-select-trigger--rich"
                aria-haspopup="listbox"
                aria-expanded={accountDropdownOpen}
                onClick={() => setAccountDropdownOpen((prev) => !prev)}
              >
                {selectedAccountMeta ? (
                  <span className="ui-select-rich">
                    {/* Avatar — same treatment as the account cards in «Аккаунты». */}
                    <span style={{
                      width: isPhoneLayout ? "40px" : "36px",
                      height: isPhoneLayout ? "40px" : "36px",
                      flexShrink: 0,
                      borderRadius: "10px",
                      background: aa(500, 0.14),
                      border: `1px solid ${aa(500, 0.28)}`,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: A[300],
                      fontSize: "14px",
                      fontWeight: 700
                    }}>
                      {String(formatAccountLabel(selectedAccountMeta) || "?").trim().charAt(0).toUpperCase() || "?"}
                    </span>
                    <span className="ui-select-option-text">
                      <span className="ui-select-option-label">{formatAccountLabel(selectedAccountMeta)}</span>
                      <span className="ui-select-option-sub">{getAccountProxyLabel(selectedAccountMeta)}</span>
                    </span>
                    <SatisfactionBadge value={normalizeSatisfactionBadge(selectedAccountMeta.satisfactionBadge)} />
                  </span>
                ) : (
                  <span className="ui-select-value is-placeholder">{t("Выберите аккаунт")}</span>
                )}
                <svg className="ui-select-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {accountDropdownOpen && (
                <div className="ui-select-menu" role="listbox" data-modal-scroll="1">
                  <button
                    type="button"
                    role="option"
                    aria-selected={!newAd.accountId}
                    className={`ui-select-option ui-select-option--hover${!newAd.accountId ? " is-selected" : ""}`}
                    onClick={() => {
                      setNewAd((prev) => ({ ...prev, accountId: "" }));
                      setAccountDropdownOpen(false);
                    }}
                  >
                    <span className="ui-select-option-text"><span className="ui-select-option-label">{t("Без аккаунта")}</span></span>
                    {!newAd.accountId && <span className="ui-select-check"><CheckIcon size={15} /></span>}
                  </button>
                  {accounts.map((account) => {
                    const isSelected = String(account.id) === String(newAd.accountId);
                    const badge = normalizeSatisfactionBadge(account.satisfactionBadge);
                    return (
                      <button
                        key={account.id}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={`ui-select-option ui-select-option--hover${isSelected ? " is-selected" : ""}`}
                        onClick={() => {
                          setNewAd((prev) => ({ ...prev, accountId: String(account.id) }));
                          setAccountDropdownOpen(false);
                        }}
                      >
                        <span className="ui-select-option-text">
                          <span className="ui-select-option-label">{formatAccountLabel(account)}</span>
                          <span className="ui-select-option-sub">{getAccountProxyLabel(account)}</span>
                        </span>
                        <SatisfactionBadge value={badge} />
                        <span className="ui-select-check" style={{ visibility: isSelected ? "visible" : "hidden" }}><CheckIcon size={15} /></span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div style={sectionCardStyle}>
            <label style={labelStyle}>{t("Категория")}</label>

            {/* Collapsed row: path on top, selected leaf below, chevron right. */}
            <button
              type="button"
              onClick={() => setCategoryPickerOpen((prev) => !prev)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "10px 12px",
                borderRadius: "13px",
                border: "1px solid rgba(141,147,163,0.24)",
                background: "rgba(8,9,12,0.55)",
                cursor: "pointer",
                textAlign: "left",
                fontFamily: "inherit",
                transition: "border-color 0.15s ease"
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = aa(400, 0.45); }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(141,147,163,0.24)"; }}
            >
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: "block", fontSize: "10.5px", color: "#707684", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {categoryTrailLabel}
                </span>
                <span style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#e2e4e9", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {categoryLeafLabel}
                </span>
              </span>
              <span style={{
                flexShrink: 0,
                color: A[300],
                display: "inline-flex",
                transform: categoryPickerOpen ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform 0.15s ease"
              }}>
                <ChevronRightIcon size={15} />
              </span>
            </button>

            {categoryPickerOpen && (
            <div style={{
              border: "1px solid rgba(141,147,163,0.25)",
              borderRadius: "18px",
              background: sa(0.45),
              marginTop: "10px",
              overflow: "hidden"
            }}>
              {/* Поиск по всему дереву. Уровней у Kleinanzeigen два, всего около
                  полутора сотен категорий — искать быстрее, чем ходить по ним. */}
              <div style={{ padding: "12px", borderBottom: "1px solid rgba(141,147,163,0.16)" }}>
                <div style={{ position: "relative" }}>
                  <span style={{
                    position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)",
                    color: "#707684", display: "inline-flex", pointerEvents: "none"
                  }}>
                    <SearchIcon size={15} />
                  </span>
                  <input
                    type="text"
                    autoFocus
                    value={categorySearch}
                    onChange={(event) => setCategorySearch(event.target.value)}
                    placeholder={t("Поиск категории")}
                    style={{
                      ...inputStyle,
                      padding: "10px 34px 10px 34px",
                      fontSize: "13px",
                      borderRadius: "13px"
                    }}
                  />
                  {categorySearch && (
                    <button
                      type="button"
                      className="kl-plain-button"
                      onClick={() => setCategorySearch("")}
                      title={t("Очистить")}
                      style={{
                        position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)",
                        border: "none", background: "transparent", color: "#8d93a3",
                        cursor: "pointer", display: "inline-flex", padding: "4px"
                      }}
                    >
                      <XIcon size={13} />
                    </button>
                  )}
                </div>
              </div>

              {/* Подсказка модели по названию объявления. */}
              {String(newAd?.title || "").trim().length >= 4 && (
                <div style={{
                  padding: "10px 12px",
                  borderBottom: "1px solid rgba(141,147,163,0.16)",
                  background: aa(900, 0.16),
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px"
                }}>
                  {/* Шапка и кнопка — своей строкой, чтобы предложения ниже шли
                      такими же строками, как обычные категории в списке. */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", minHeight: "26px" }}>
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: "6px",
                      fontSize: "11px", fontWeight: 700, letterSpacing: "0.04em",
                      textTransform: "uppercase", color: A[300], flexShrink: 0
                    }}>
                      <SparklesIcon size={13} />
                      {categorySuggest.status === "loading" ? t("Подбираем...") : t("Похоже на")}
                    </span>

                    {categorySuggest.status === "loading" && (
                      <span style={{ fontSize: "12px", color: "#8d93a3" }}>{t("по названию объявления")}</span>
                    )}

                    {categorySuggest.status === "idle" && (
                      <span style={{ fontSize: "12px", color: "#8d93a3" }}>
                        {t("нажмите «Подобрать»")}
                      </span>
                    )}

                    {categorySuggest.status === "error" && (
                      <span style={{ fontSize: "12px", color: "#8d93a3" }}>
                        {categorySuggest.error || t("Подходящая категория не нашлась")}
                      </span>
                    )}

                    {categorySuggest.status !== "loading" && (
                    <button
                      type="button"
                      onClick={() => requestCategorySuggestions()}
                      style={{ ...aiChipStyle(true), marginLeft: "auto" }}
                      {...aiChipHover(true)}
                    >
                      {categorySuggest.status === "idle" ? t("Подобрать") : t("Подобрать заново")}
                    </button>
                    )}
                  </div>

                  {/* Предложения — той же формы, что строки списка ниже: во всю
                      ширину, с тем же скруглением и отступами, раздел мелким
                      сверху, название под ним. Отличает их только подсветка. */}
                  {categorySuggest.status === "done" && categorySuggest.items.map((item, index) => {
                    const parts = String(item.path || "").split(">").map((part) => catText(part.trim())).filter(Boolean);
                    const name = parts[parts.length - 1] || "";
                    const trail = parts.slice(0, -1).join(" › ");
                    const background = index === 0 ? aa(600, 0.3) : aa(800, 0.2);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => applyCategorySuggestion(item)}
                        title={t("Выбрать эту категорию")}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "9px 11px",
                          borderRadius: "10px",
                          border: `1px solid ${index === 0 ? aa(400, 0.42) : aa(400, 0.2)}`,
                          background,
                          color: "#e2e4e9",
                          cursor: "pointer",
                          fontFamily: "inherit",
                          fontSize: "13px",
                          display: "block",
                          transition: "background 0.15s ease, border-color 0.15s ease"
                        }}
                        onMouseEnter={(event) => {
                          event.currentTarget.style.background = aa(600, 0.38);
                          event.currentTarget.style.borderColor = aa(400, 0.55);
                        }}
                        onMouseLeave={(event) => {
                          event.currentTarget.style.background = background;
                          event.currentTarget.style.borderColor = index === 0 ? aa(400, 0.42) : aa(400, 0.2);
                        }}
                      >
                        <span style={{
                          display: "block", color: A[300], fontSize: "11px",
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
                        }}>
                          {trail || t("Раздел")}
                        </span>
                        <span style={{
                          display: "block", fontWeight: index === 0 ? 700 : 600,
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
                        }}>
                          {name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              <div ref={categoryListRef} style={{ maxHeight: "340px", overflowY: "auto", padding: "6px" }}>
                {categorySearchNormalized ? (
                  categorySearchMatches.length ? (
                    categorySearchMatches.map((option) => {
                      const isSelected = String(newAd?.categoryId || "") === String(option.value);
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => { handleQuickCategorySelect(option); setCategoryPickerOpen(false); }}
                          style={{
                            width: "100%", textAlign: "left", padding: "9px 11px",
                            borderRadius: "10px", border: "1px solid transparent",
                            background: isSelected ? aa(600, 0.22) : "transparent",
                            color: "#e2e4e9", cursor: "pointer", fontFamily: "inherit",
                            fontSize: "13px", display: "block"
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = aa(800, 0.28); }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? aa(600, 0.22) : "transparent"; }}
                        >
                          <span style={{ color: "#8d93a3", fontSize: "11px" }}>
                            {option.fullPathLabel.split(" / ").slice(0, -1).join(" › ") || t("Раздел")}
                          </span>
                          <span style={{ display: "block", fontWeight: 600 }}>{option.label}</span>
                        </button>
                      );
                    })
                  ) : (
                    <div style={{ padding: "18px 12px", color: "#8d93a3", fontSize: "13px", textAlign: "center" }}>
                      {t("Категории не найдены")}
                    </div>
                  )
                ) : (
                  localCategories.map((section) => {
                    const sectionLabel = getCategoryLabel(section);
                    const children = Array.isArray(section.children) ? section.children : [];
                    const sectionValue = getCategoryValue(section);
                    return (
                      <div key={sectionValue || sectionLabel} style={{ marginBottom: "4px" }}>
                        <div style={{
                          position: "sticky", top: 0, zIndex: 1,
                          padding: "7px 11px 5px",
                          fontSize: "11px", fontWeight: 700, letterSpacing: "0.04em",
                          textTransform: "uppercase", color: "#707684",
                          background: sa(0.92), backdropFilter: "blur(2px)"
                        }}>
                          {sectionLabel}
                        </div>
                        {(children.length ? children : [section]).map((node) => {
                          const value = getCategoryValue(node);
                          const isSelected = String(newAd?.categoryId || "") === String(value);
                          return (
                            <button
                              key={value || getCategoryLabel(node)}
                              type="button"
                              onClick={() => {
                                handleQuickCategorySelect({
                                  value: String(value),
                                  label: getCategoryLabel(node),
                                  pathValues: children.length ? [String(sectionValue), String(value)] : [String(value)],
                                  fullPathLabel: children.length
                                    ? `${sectionLabel} / ${getCategoryLabel(node)}`
                                    : sectionLabel
                                });
                                setCategoryPickerOpen(false);
                              }}
                              style={{
                                width: "100%", textAlign: "left", padding: "8px 11px",
                                borderRadius: "10px", border: "1px solid transparent",
                                background: isSelected ? aa(600, 0.22) : "transparent",
                                color: "#e2e4e9", cursor: "pointer", fontFamily: "inherit",
                                fontSize: "13px", display: "block"
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = aa(800, 0.28); }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? aa(600, 0.22) : "transparent"; }}
                            >
                              {getCategoryLabel(node)}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })
                )}
              </div>

              {categoryChildrenError && (
                <div style={{ color: "#fca5a5", fontSize: "12px", padding: "0 12px 10px" }}>
                  {categoryChildrenError}
                </div>
              )}
            </div>
            )}
          </div>

          <div style={sectionCardStyle}>
            <label style={labelStyle}>{t("Цена")}</label>
            {/* Цена (design 2a §56): amount with an inline € and a Festpreis/VB
                segmented switch instead of a dropdown. */}
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: isPhoneLayout ? "wrap" : "nowrap" }}>
              <div style={{ position: "relative", flex: "1 1 auto", minWidth: 0, width: isPhoneLayout ? "100%" : undefined }}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newAd.price}
                  onChange={(e) => setNewAd((prev) => ({ ...prev, price: normalizePriceInput(e.target.value) }))}
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder="0.00"
                  style={{
                    ...inputStyle,
                    width: "100%",
                    height: isPhoneLayout ? "50px" : "44px",
                    borderRadius: "13px",
                    fontSize: isPhoneLayout ? "17px" : "15px",
                    fontWeight: 600,
                    padding: "0 36px 0 13px"
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = inputFocusColor}
                  onBlur={(e) => e.currentTarget.style.borderColor = inputBlurColor}
                />
                <span style={{
                  position: "absolute",
                  right: "13px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: A[200],
                  fontSize: "13.5px",
                  fontWeight: 700,
                  pointerEvents: "none"
                }}>€</span>
              </div>
              <div style={{
                display: "flex",
                padding: "3px",
                borderRadius: "13px",
                background: "rgba(8,9,12,0.55)",
                border: "1px solid rgba(141,147,163,0.24)",
                flexShrink: 0,
                width: isPhoneLayout ? "100%" : undefined
              }}>
                {[{ value: "FIXED", label: "Festpreis" }, { value: "NEGOTIABLE", label: "VB" }].map((opt) => {
                  const active = String(newAd.priceType || "FIXED") === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setNewAd((prev) => ({ ...prev, priceType: opt.value }))}
                      style={{
                        flex: isPhoneLayout ? "1 1 0" : "0 0 auto",
                        height: isPhoneLayout ? "40px" : "38px",
                        padding: "0 14px",
                        borderRadius: "10px",
                        border: "none",
                        background: active ? aa(600, 0.9) : "transparent",
                        color: active ? onAccent(A[50]) : "#8d93a3",
                        fontFamily: "inherit",
                        fontSize: "12.5px",
                        fontWeight: active ? 700 : 600,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        transition: "background 0.15s ease, color 0.15s ease"
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {loadingExtraFields && (
            <div style={{
              ...sectionCardStyle,
              color: "#8d93a3",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}>
              <LoadingIcon size={14} />
              {t("Загрузка параметров категории...")}
            </div>
          )}

          {!loadingExtraFields && extraFieldsError && (
            <div style={{
              ...sectionCardStyle,
              color: "#fca5a5",
              fontSize: "12px",
              border: "1px solid rgba(248,113,113,0.3)"
            }}>
              {t("Не удалось загрузить параметры категории:")} {extraFieldsError}
            </div>
          )}

          {!loadingExtraFields && !extraFieldsError && (newAd.categoryId || newAd.categoryUrl) && (!extraFields || extraFields.length === 0) && (
            <div style={{ ...sectionCardStyle, color: newAd.accountId ? "#8d93a3" : "#fde68a", fontSize: "12px" }}>
              {newAd.accountId
                ? t("Для выбранной категории нет дополнительных параметров.")
                : t("Выберите аккаунт, чтобы загрузить параметры категории.")}
            </div>
          )}


          <div style={sectionCardStyle}>
            <label style={labelStyle}>PLZ</label>
            {/* Reference: one row — narrow PLZ field plus the two fill buttons. */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="text"
                value={newAd.postalCode}
                onChange={(e) => { const value = e.target.value; setNewAd((prev) => withPostalCode(prev, value)); }}
                placeholder="PLZ"
                style={{
                  ...inputStyle,
                  width: isPhoneLayout ? "96px" : "110px",
                  flex: "0 0 auto",
                  height: "44px",
                  borderRadius: "13px",
                  fontSize: isPhoneLayout ? "16px" : "14px"
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = inputFocusColor}
                onBlur={(e) => e.currentTarget.style.borderColor = inputBlurColor}
              />
              {/* Подставить PLZ — второстепенные кнопки панели: прозрачные, с
                  тонкой рамкой, значки — как у разделов в сайдбаре. Полная
                  фраза — в подсказке, на кнопке ей не хватало места. */}
              {[
                { key: "account", label: t("Аккаунт"), hint: t("Подставить PLZ аккаунта"), Icon: UserIcon, onClick: fetchAccountPostalCode, busy: loadingPlz },
                { key: "proxy", label: t("Прокси"), hint: t("Подставить PLZ прокси"), Icon: LinkIcon, onClick: fetchProxyPostalCode, busy: loadingProxyPlz }
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="plz-fill-btn"
                  onClick={item.onClick}
                  disabled={item.busy}
                  title={item.hint}
                  aria-label={item.hint}
                >
                  {item.busy ? <LoadingIcon size={15} /> : <item.Icon size={15} />}
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
            {plzPlace.city && plzPlace.code === String(newAd.postalCode || "").trim() ? (
              <div className="plz-city-hint" style={{
                display: "flex", alignItems: "center", gap: "5px",
                marginTop: "7px", paddingLeft: "2px",
                fontSize: "11.5px", lineHeight: 1.25, color: "#8d93a3",
                letterSpacing: "0.01em",
                animation: "fadeInTab 0.24s ease-out"
              }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.75 }}>
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0" /><circle cx="12" cy="10" r="3" />
                </svg>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {plzPlace.state && plzPlace.state !== plzPlace.city ? `${plzPlace.city} · ${plzPlace.state}` : plzPlace.city}
                </span>
              </div>
            ) : null}
            {plzLocations.code === String(newAd.postalCode || "").trim() && plzLocations.items.length > 1 ? (
              <div className="plz-location-pick" style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "6px", animation: "fadeInTab 0.24s ease-out" }}>
                <span style={{ fontSize: "12px", color: "#8d93a3" }}>
                  {t("Населённый пункт")}
                  <span style={{ opacity: 0.7 }}>{` · ${tf`${plzLocations.items.length} у этого PLZ`}`}</span>
                </span>
                <StyledSelect
                  value={String(newAd.locationId || "")}
                  onChange={(nextId) => {
                    const picked = plzLocations.items.find((item) => item.id === String(nextId));
                    setNewAd((prev) => ({ ...prev, locationId: String(nextId || ""), locationName: picked?.name || "" }));
                  }}
                  options={plzLocations.items.map((item) => ({ value: item.id, label: item.name }))}
                  placeholder={t("Выберите")}
                  ariaLabel={t("Населённый пункт")}
                />
              </div>
            ) : null}
          </div>
          </div>

          {(() => {
            const selectedAcc = accounts.find((a) => String(a.id) === String(newAd.accountId));
            // Тип мог быть записан и как «COMMERCIAL» — это тот же gewerblich, и
            // поле Impressum ему нужно так же (без него KL отклоняет объявление).
            const selectedType = String(selectedAcc?.accountType || "").trim().toLowerCase();
            if (selectedType !== "gewerblich" && selectedType !== "commercial") return null;
            return (
              <div style={sectionCardStyle}>
                <label style={labelStyle}>{t("Юридическая информация")}</label>
                <textarea
                  value={newAd.legalInfo || ""}
                  onChange={(e) => setNewAd((prev) => ({ ...prev, legalInfo: e.target.value }))}
                  placeholder="Impressum / Rechtliche Angaben..."
                  rows="4"
                  style={{
                    ...inputStyle,
                    minHeight: "100px",
                    resize: "vertical",
                    fontFamily: "inherit"
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = inputFocusColor}
                  onBlur={(e) => e.currentTarget.style.borderColor = inputBlurColor}
                />
              </div>
            );
          })()}


          </div>
          </div>

          {/* Footer (design 2a): required-field status on the left, actions on the
              right. Fixed 70px on desktop, 1px top border. */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: isPhoneLayout ? "8px" : "12px",
            rowGap: "8px",
            flexWrap: isPhoneLayout ? "wrap" : "nowrap",
            height: isPhoneLayout ? "auto" : "70px",
            padding: isPhoneLayout ? "12px 0 12px" : "0 24px",
            flexShrink: 0,
            borderTop: "1px solid rgba(141,147,163,0.14)",
            ...(isPhoneLayout ? { position: "sticky", bottom: 0, background: S.surface, zIndex: 3 } : {})
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "7px",
              minWidth: 0,
              flex: isPhoneLayout ? "1 1 100%" : "0 1 auto",
              fontSize: "12.5px",
              color: notReadyToPublish ? "#fde68a" : "#4ade80"
            }}>
              <AlertIcon size={15} color="currentColor" />
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {notReadyToPublish ? missingRequiredLabel : t("Готово к публикации")}
              </span>
            </div>
            {(
              <div style={{
                // On mobile the buttons flow directly into the footer flex row
                // (contents) so «Опубликовать» can take its own full-width row.
                display: isPhoneLayout ? "contents" : "flex",
                gap: "10px",
                alignItems: "center",
                justifyContent: "flex-end"
              }}>
            <button
              type="button"
              className="kl-plain-button"
              onClick={onClose}
              // Reference: on phones the header ✕ closes the modal, the footer
              // keeps only Опубликовать / В черновик / Предпросмотр.
              style={{ ...ghostButtonStyle, display: isPhoneLayout ? "none" : "inline-flex", padding: "0 18px", height: "44px", borderRadius: "12px", fontSize: "14px", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
              onMouseEnter={(event) => applyHoverStyles(event, ghostHoverEnter)}
              onMouseLeave={(event) => applyHoverStyles(event, ghostHoverLeave)}
            >
              {t("Отмена")}
            </button>
                {/* Reference: desktop footer is Отмена / В черновик / Опубликовать
                    only — preview is a phone-only secondary action. */}
                {isPhoneLayout && (
                  <button
                    type="button"
                    onClick={() => setShowPreview(true)}
                    className="kl-plain-button"
                    style={{ ...ghostButtonStyle, padding: "0 12px", height: "44px", borderRadius: "13px", fontSize: "14px", fontWeight: 600, flex: "1 1 0", minWidth: 0, order: 3 }}
                    onMouseEnter={(event) => applyHoverStyles(event, ghostHoverEnter)}
                    onMouseLeave={(event) => applyHoverStyles(event, ghostHoverLeave)}
                  >
                    {t("Предпросмотр")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={onSaveDraft}
                  className="kl-plain-button"
                  disabled={publishing}
                  style={{ ...ghostButtonStyle, padding: isPhoneLayout ? "0 12px" : "0 16px", height: "44px", borderRadius: isPhoneLayout ? "13px" : "12px", fontSize: "14px", fontWeight: 600, cursor: publishing ? "not-allowed" : "pointer", opacity: publishing ? 0.7 : 1, whiteSpace: "nowrap", flex: isPhoneLayout ? "1 1 0" : "0 0 auto", minWidth: 0, overflow: "hidden", order: isPhoneLayout ? 2 : undefined }}
                  onMouseEnter={(event) => { if (!publishing) applyHoverStyles(event, ghostHoverEnter); }}
                  onMouseLeave={(event) => { if (!publishing) applyHoverStyles(event, ghostHoverLeave); }}
                >
                  <FileIcon size={15} /> {t("В черновик")}
                </button>
                <button
                  type="button"
                  onClick={handlePublishClick}
                  className="kl-plain-button kl-press-send ad-accent-btn"
                  disabled={publishing}
                  style={{ ...accentButtonStyle, padding: "0 24px", height: isPhoneLayout ? "48px" : "44px", borderRadius: isPhoneLayout ? "14px" : "12px", fontSize: "14px", cursor: publishing ? "not-allowed" : "pointer", opacity: publishing ? 0.7 : 1, whiteSpace: "nowrap", flex: isPhoneLayout ? "1 1 100%" : undefined, justifyContent: isPhoneLayout ? "center" : undefined, order: isPhoneLayout ? 1 : undefined }}
                  onMouseEnter={(event) => { if (!publishing) applyHoverStyles(event, accentHoverEnter); }}
                  onMouseLeave={(event) => { if (!publishing) applyHoverStyles(event, accentHoverLeave); }}
                >
                  {publishing
                    ? (<><LoadingIcon size={15} /> {t("Публикация...")}</>)
                    : (<><span className="kl-btn-ico"><SendIcon size={15} /></span> {t("Опубликовать")}</>)}
                </button>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {photoEditorItem && (() => {
        const editedFile = photoEditorItem.source === "local"
          ? (Array.isArray(adImages) ? adImages[photoEditorItem.localIndex] : null)
          : null;
        return (
          <PhotoEditor
            key={photoEditorItem.key}
            source={editedFile || photoEditorItem.url}
            fileName={editedFile?.name || `photo-${(photoEditorItem.draftIndex ?? 0) + 1}.jpg`}
            onCancel={() => setPhotoEditorItem(null)}
            onSave={(file) => {
              replaceEditedPhoto(photoEditorItem, file);
              setPhotoEditorItem(null);
            }}
          />
        );
      })()}

      {previewPresence.mounted && (
        <AdPreviewDialog
          presenceState={previewPresence.state}
          isPhone={isPhoneLayout}
          photos={combinedImageItems}
          price={newAd.price}
          priceType={newAd.priceType}
          title={newAd.title}
          categoryPath={previewCategoryPath}
          postalCode={newAd.postalCode}
          city={plzPlace.code === String(newAd.postalCode || "").trim() ? plzPlace.city : ""}
          accountName={selectedAccountMeta ? previewAccountName : ""}
          accountEmail={previewAccountEmail}
          scheduledAt={newAd.scheduledAt}
          scheduledLabel={newAd.scheduledAt ? new Date(newAd.scheduledAt).toLocaleString(locale(), { timeZone: "Europe/Berlin" }) : ""}
          description={newAd.description}
          params={filledPreviewExtraFields}
          missingLabel={notReadyToPublish ? missingRequiredLabel : ""}
          publishing={publishing}
          onClose={() => setShowPreview(false)}
          onSaveDraft={onSaveDraft}
          onPublish={() => {
            // Как кнопка формы: недостающие параметры подсвечиваются в форме,
            // поэтому предпросмотр сначала закрывается.
            setShowPreview(false);
            handlePublishClick();
          }}
        />
      )}
      <style>{`
        .ad-modal-root .kl-plain-button {
          transition: all 0.2s ease !important;
        }
        .ad-modal-root .kl-plain-button:hover:not(:disabled) {
          background: linear-gradient(135deg, ${aa(700, 0.38)}, ${aa(600, 0.26)}) !important;
          border-color: ${aa(300, 0.56)} !important;
          box-shadow: 0 14px 28px ${aa(900, 0.24)} !important;
          transform: translateY(-2px) !important;
          color: #f3e8ff !important;
        }
        .ad-modal-root .kl-plain-button:active:not(:disabled) {
          transform: translateY(0) !important;
        }
        html[data-theme="light"] .ad-modal-root .kl-plain-button:hover:not(:disabled) {
          background: linear-gradient(135deg, ${A[100]}, ${A[50]}) !important;
          border-color: ${aa(500, 0.28)} !important;
          box-shadow: 0 8px 18px ${aa(500, 0.16)} !important;
          color: #0e1015 !important;
        }
      `}</style>
    </div>
  );
};

export default AdModal;
